// src/VaccinationDashboardPage.tsx
// Ситуационный центр «Вакцинация». Устройство экрана целиком повторяет
// DuchetDashboardPage (утверждено заказчиком на Д-учёте): та же тёмная тема, шапка с
// фильтрами, KPI-панель, сетка виджетов, разворот виджета на весь экран (⛶ / Esc),
// drill по клику (новая вкладка с таблицей), выгрузка CSV.
// Данные: /api/v1/vaccination/dashboard/{bootstrap,summary,widgets};
// drill -> /dashboards/vaccination/cards.
//
// ПУСТЫЕ ДАННЫЕ. Часть показателей МИС ещё не присылает: у таких мест бэкенд отдаёт
// значение null и поле caveat с ПРИЧИНОЙ. Виджет всё равно рисуется, число заменяется
// на «—», а текст caveat печатается мелким шрифтом под заголовком виджета — своими
// словами «нет данных» не пишем.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import "@tabler/icons-webfont/dist/tabler-icons.min.css";
import { PeriodCalendar } from "./PeriodCalendar";
import VacMapWidget from "./VacMapWidget";

// ── ТЕМА ОБОЛОЧКИ (как в остальных дашбордах) ──
const SHELL = {
  page: "#083264",
  card: "#0e2b4b",
  border: "rgba(255,255,255,0.2)",
  text: "#dce6f2",
  textMuted: "rgba(255,255,255,0.6)",
  btnBg: "#083264",
};

// ── ВНУТРЕННОСТЬ «разворота» ──
const W = {
  wrap: "#0a1220",
  card: "#101d30",
  cardBorder: "#223b57",
  track: "#0a1526",
  divider: "#1a2c44",
  rowBorder: "#16283f",
};

// ── ПАЛИТРА ДАННЫХ ──
const D = {
  green: "#25c274", greenText: "#5dcaa5",
  yellow: "#f5b423",
  red: "#f0554f", redText: "#f0908c",
  blue: "#4b9fe8", blue2: "#3f8fd9", blue3: "#2f7fca", blue4: "#185fa5",
  violet: "#8479e6", violetText: "#a79ef0",
  orange: "#e8703a",
  gray: "#9aa0aa", grayText: "#b4b2a9", grayDim: "#647a92",
  label: "#7d93ad", widgetTitle: "#a7c0dc", axis: "#6f86a1", axisVal: "#9fb3cc",
  rowText: "#c3d2e4", hint: "#54708f",
};

const TONES = {
  blue:   { bg: "#0e2540", border: "#2f6fb0", label: "#8fb4dd", sub: "#5a83ad", accent: D.blue },
  green:  { bg: "#0d2e26", border: "#1d9e75", label: "#6fd3b0", sub: "#4c8f79", accent: D.green },
  yellow: { bg: "#33280c", border: "#d19822", label: "#e2b968", sub: "#a8842f", accent: D.yellow },
  red:    { bg: "#361718", border: "#d0403c", label: "#f0908c", sub: "#b0605d", accent: D.red },
  violet: { bg: "#221d40", border: "#6a5fd8", label: "#a79ef0", sub: "#7d74c4", accent: D.violet },
  gray:   { bg: "#1a2536", border: "#4a596e", label: "#9fb0c6", sub: "#6f829a", accent: "#8fa2ba" },
} as const;
type ToneKey = keyof typeof TONES;

// ── СВЕТОФОР ОХВАТА ──────────────────────────────────────────────────────────
// Было из макета: >95 зелёный, 80–95 жёлтый, <80 красный. Эти пороги придуманы
// до того, как охват начали считать по календарю, и на реальных цифрах не работают:
// охват по району 33,6%, по амбулаториям 24–41% (замер 12.08.2026) — всё красное,
// цвет не различает ничего. Утверждённой целевой цифры у заказчика нет, поэтому
// любой АБСОЛЮТНЫЙ порог был бы выдуман нами заново.
//
// Красим ОТНОСИТЕЛЬНО РАЙОНА: зелёный — заметно лучше среднего, красный — заметно
// хуже, жёлтый — как район. Это сравнение, которое данные подтверждают, и оно
// само подстроится, когда охват вырастет.
//
// COVERAGE_BAND — ширина «как район» в долях от среднего. При 15% и среднем 33,6%
// получается: зелёный от 38,6%, красный ниже 28,6%. Одно число, чтобы менять.
const COVERAGE_BAND = 0.15;
// выполнение плана — свои пороги (как на KPI-карточке «Выполнение плана»)
function planColor(pct: number | null | undefined): string {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return D.hint;
  if (pct >= 90) return D.green;
  if (pct >= 70) return D.yellow;
  return D.red;
}
/** Цвет охвата участка/амбулатории по сравнению со средним по району.
 *  Без ориентира (район ещё не посчитан) цвет не даём — серый, а не «красный». */
function covColor(pct: number | null | undefined,
                  benchmark?: number | null): string {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return D.hint;
  if (benchmark === null || benchmark === undefined || !Number.isFinite(benchmark)
      || benchmark <= 0) return D.hint;
  if (pct >= benchmark * (1 + COVERAGE_BAND)) return D.green;
  if (pct >= benchmark * (1 - COVERAGE_BAND)) return D.yellow;
  return D.red;
}

// ── api ──
function apiBase(): string {
  const env = (import.meta as any).env;
  return String(env?.VITE_DASHBOARDS_API_BASE || "/dashboards-api").replace(/\/+$/, "");
}
function publicBase(): string {
  const env = (import.meta as any).env;
  return String(env?.VITE_DASHBOARDS_PUBLIC_BASE_URL || env?.VITE_DASHBOARDS_PUBLIC_BASE || "").replace(/\/+$/, "") || window.location.origin;
}
function authHeaders(): Record<string, string> {
  const h: Record<string, string> = { Accept: "application/json" };
  try {
    const raw = localStorage.getItem("auth_payload") || localStorage.getItem("dashboards_auth") || localStorage.getItem("auth");
    if (raw) { const a = JSON.parse(raw); if (a?.access_token) h.Authorization = `Bearer ${a.access_token}`; if (a?.iin) h["X-User-IIN"] = a.iin; }
  } catch { /* ignore */ }
  const role = (localStorage.getItem("dashboards_role") || "").trim();
  if (role) h["X-Role"] = role;
  return h;
}
async function api<T>(path: string, params: Record<string, any>): Promise<T> {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") qs.set(k, String(v)); });
  const res = await fetch(`${apiBase()}${path}?${qs.toString()}`, { headers: authHeaders(), credentials: "omit" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

type OrgOption = { id: string; label: string };
async function fetchOrgs(): Promise<OrgOption[]> {
  const res = await fetch(`/api/v1/analytics/dicts`, { headers: authHeaders(), credentials: "omit" });
  if (!res.ok) throw new Error(`${res.status}`);
  const data = (await res.json()) as { orgs?: Array<Record<string, unknown>> };
  return (Array.isArray(data?.orgs) ? data.orgs : [])
    .map((o) => ({ id: String(o?.orgId || "").trim(), label: String(o?.name || o?.orgName || o?.shortName || o?.orgId || "").trim() }))
    .filter((o) => o.id);
}

// ── типы ответов ──
type Bootstrap = {
  mis_id: string; areas: string[]; contingents: string[]; vac_types: string[];
  preparations: string[]; places?: string[];
  doctors: { post_id: string; fio: string }[]; years: number[];
};
type Summary = {
  period: { from: string; to: string };
  cards: {
    done: { today: number | null; week: number | null; period: number | null; caveat?: string };
    plan: { fact: number | null; plan: number | null; pct: number | null;
            pct_to_date: number | null; overdue: number | null; not_due_yet: number | null;
            year?: number; stub?: string | null; caveat?: string };
    // due — у кого срок уже наступил, full — у кого выполнены ВСЕ такие требования.
    // Раньше тут стояли population/covered, которых бэкенд никогда не присылал,
    // и подпись карточки показывала «привито — из —».
    coverage: { pct: number | null; due: number | null; full: number | null;
                partial: number | null; none: number | null;
                stub?: string | null; caveat?: string };
    overdue: { total: number | null; not_done_status?: number | null;
               children: number | null; adults: number | null; caveat?: string };
    refusals: { total: number | null; pct_of_invited: number | null; caveat?: string };
  };
};
type FacilityRow = { name: string; done: number; people: number; population: number | null;
                     coverage: number | null; plan_pct?: number | null;
                     // covered/due — числитель и знаменатель охвата по календарю;
                     // vaccinated_pct — доля населения с ЛЮБОЙ прививкой за период,
                     // это НЕ охват (раньше именно она и называлась охватом)
                     covered?: number | null; due?: number | null;
                     plan?: number | null; fact?: number | null;
                     vaccinated_pct?: number | null };
type Widgets = {
  period: { from: string; to: string };
  w1_plan: { fact: number | null; plan: number | null; pct: number | null; left: number | null;
             dynamics: { week: string; done: number; pct: number | null }[]; caveat?: string };
  w2_age_groups: { groups: { group: string; short?: string; code?: string;
                             plan: number | null; fact: number | null; pct: number | null;
                             covered?: number | null; due?: number | null }[];
                   total_pct: number | null; caveat?: string };
  w3_map: { facilities: FacilityRow[]; areas?: FacilityRow[];
            unmapped?: { areas: number; people: number }; caveat?: string };
  w4_overdue: { groups: { group: string; n: number; people?: number }[];
                total: number | null; people?: number | null; year?: number;
                not_done_status?: { total: number | null; caveat?: string };
                stub?: string | null; caveat?: string };
  w5_rating: { rows: FacilityRow[]; min_due?: number;
               plan_caveat?: string; caveat?: string };
  w6_refusals: { reasons: { reason: string; n: number }[]; dynamics: { month: string; n: number }[];
                 total: number | null; caveat?: string };
  w7_staff: { doctors: { post_id: string; fio: string; n: number }[];
              avg_per_day: number | null; caveat?: string };
  // Место вакцинации: МИС его отдельным полем не присылает, восстановлено
  // по подразделению врача (см. vac_engine.CURRENT_POSITION).
  w9_place?: { places: { place: string; done: number; people: number; pct: number | null }[];
               total: number; unknown: number; caveat?: string };
  w8_critical: { overdue_7d: number | null; overdue_7d_people?: number | null;
                 overdue_7d_caveat?: string;
                 children_no_vac: number | null; children_no_vac_of?: number | null;
                 children_no_vac_caveat?: string;
                 low_coverage_areas: number | null; low_coverage_of?: number | null;
                 district_pct?: number | null; low_coverage_caveat?: string;
                 worst_areas?: { name: string; coverage: number | null; population: number | null }[];
                 refusals: number | null;
                 visit_pct: number | null; visit_caveat?: string };
};

// ── 10. Приёмы по иммунопрофилактике ──
// Источник не прививочный: это протоколы осмотра из МИС с поводом обращения
// «Иммунопрофилактика», которые МИС присылает дефектологу на проверку. Приём —
// это человек и день (у одного человека за день несколько документов: осмотр
// врача и услуга вакцинации приходят отдельными строками).
// `known` — приёмы в днях, по которые выгрузка прививок от МИС уже дошла;
// `stale` — весь столбец лежит за этой границей, доля по нему не считается
// (иначе свежие дни показывали бы «0% с прививкой» просто из-за отставания выгрузки).
type ImmunoPoint = { key: string; label: string; from: string; to: string;
  visits: number; docs: number; with_vac: number; known: number; stale: boolean;
  pct: number | null };
type ImmunoPlace = { place: string; visits: number; known: number; with_vac: number; pct: number | null };
type Immuno = {
  granularity: "day" | "month";
  points: ImmunoPoint[];
  vac_until?: string | null;
  total: { visits: number; docs: number; people: number; known: number;
           with_vac: number; pct: number | null };
  by_place: ImmunoPlace[];
  caveat?: string | null;
};

const MONTHS = ["Янв", "Фев", "Мар", "Апр", "Май", "Июн", "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек"];

// ── форматирование: разряды через NBSP, проценты с запятой, пусто → «—» ──
const NBSP = " ";
function fmt(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}
function fmtNum(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return String(n).replace(".", ",");
}
function fmtPct(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  return v.toFixed(1).replace(".", ",") + "%";
}
function shortDate(iso: string): string {
  const s = String(iso || "").slice(0, 10).split("-");
  return s.length === 3 ? `${s[2]}.${s[1]}` : String(iso);
}
// Точка графика приходит полуинтервалом [from, to), а фильтр таблицы понимает
// date_to включительно — иначе в срез попадал бы лишний день.
function dayBefore(iso: string): string {
  const d = new Date(String(iso).slice(0, 10) + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}
function monthLabel(ym: string): string {
  const [y, m] = String(ym || "").split("-");
  const i = Number(m) - 1;
  return MONTHS[i] ? `${MONTHS[i]} ${String(y).slice(2)}` : String(ym);
}

// ── глобальный CSS ──
const CSS = `
.vac-kpi { display:grid; grid-template-columns:repeat(5,1fr); gap:8px; margin-bottom:9px; }
.vac-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:9px; align-items:stretch; }
.vac-col { display:flex; flex-direction:column; gap:9px; }
.vac-col > .vac-widget { flex:1 1 0; display:flex; flex-direction:column; }
/* Третья колонка: карте больше высоты, критическим индикаторам меньше. Они делили
   колонку поровну, из-за чего карта была тесной, а шесть плиток занимали столько же
   места. Карта примерно вдвое выше блока индикаторов. */
.vac-col:nth-child(3) > .vac-widget:nth-child(1) { flex:2 1 0; }
.vac-col:nth-child(3) > .vac-widget:nth-child(2) { flex:0.9 1 0; }
@media (max-width:1280px) {
  .vac-kpi { grid-template-columns:repeat(3,1fr); }
  .vac-grid { grid-template-columns:repeat(2,1fr); }
}
@media (max-width:900px) {
  .vac-kpi { grid-template-columns:repeat(2,1fr); }
  .vac-grid { grid-template-columns:1fr; }
}
.vac-exp { position:absolute; top:9px; right:9px; font-size:13px; color:${D.hint}; cursor:pointer; background:none; border:none; padding:2px; line-height:1; }
.vac-exp:hover { color:${D.axisVal}; }
.vac-drillable { cursor:pointer; transition:filter .12s; }
.vac-drillable:hover { filter:brightness(1.14); }
.vac-row-drill { cursor:pointer; }
.vac-row-drill:hover { background:rgba(255,255,255,0.06); }
@keyframes vac-shimmer { 0% { opacity:.45; } 50% { opacity:.9; } 100% { opacity:.45; } }
.vac-skel { animation:vac-shimmer 1.2s ease-in-out infinite; background:${W.track}; border-radius:6px; }
@keyframes vac-pop { from { opacity:0; transform:scale(.97); } to { opacity:1; transform:scale(1); } }
.vac-full-card { animation:vac-pop .2s ease; }
.vac-widget { position:relative; background:${W.card}; border:0.5px solid ${W.cardBorder}; border-radius:10px; padding:11px; }
.vac-select { height:34px; overflow:hidden; white-space:nowrap; border-radius:8px; border:1px solid rgba(255,255,255,0.3); background:${SHELL.btnBg}; color:#fff; padding:0 8px; font-size:13px; flex:0 1 auto; min-width:0; max-width:180px; text-overflow:ellipsis; }
.vac-btn { height:34px; border-radius:8px; border:1px solid rgba(255,255,255,0.3); background:${SHELL.btnBg}; color:#fff; padding:0 12px; cursor:pointer; font-size:13px; }
.vac-scroll { scrollbar-width:thin; scrollbar-color:rgba(255,255,255,0.22) transparent; padding-right:16px; }
.vac-scroll::-webkit-scrollbar { width:9px; }
.vac-scroll::-webkit-scrollbar-track { background:transparent; }
.vac-scroll::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.18); border-radius:6px; border:2px solid transparent; background-clip:content-box; }
.vac-scroll::-webkit-scrollbar-thumb:hover { background:rgba(255,255,255,0.32); border:2px solid transparent; background-clip:content-box; }
.vac-scroll-thin { scrollbar-width:thin; scrollbar-color:rgba(255,255,255,0.22) transparent; }
.vac-scroll-thin::-webkit-scrollbar { width:9px; height:9px; }
.vac-scroll-thin::-webkit-scrollbar-track { background:transparent; }
.vac-scroll-thin::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.18); border-radius:6px; border:2px solid transparent; background-clip:content-box; }
.vac-full-card { scrollbar-width:thin; scrollbar-color:rgba(255,255,255,0.22) transparent; }
.vac-full-card::-webkit-scrollbar { width:9px; }
.vac-full-card::-webkit-scrollbar-track { background:transparent; }
.vac-full-card::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.18); border-radius:6px; border:2px solid transparent; background-clip:content-box; }
`;
function GlobalCss(): JSX.Element { return <style>{CSS}</style>; }

function WTitle({ stripe, children }: { stripe: string; children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 10.5, color: D.widgetTitle, marginBottom: 9, fontWeight: 500,
      borderLeft: `3px solid ${stripe}`, paddingLeft: 7, textTransform: "uppercase", letterSpacing: 0.3 }}>
      {children}
    </div>
  );
}

// Причина отсутствия данных — ТОЛЬКО текстом с сервера (поле caveat), под заголовком.
function Caveat({ text, expanded }: { text?: string | null; expanded?: boolean }) {
  if (!text) return null;
  return (
    <div style={{ fontSize: expanded ? 12 : 9.5, color: D.hint, lineHeight: 1.4,
      margin: "-4px 0 8px", paddingLeft: 10 }}>
      <i className="ti ti-info-circle" style={{ fontSize: expanded ? 12 : 10, marginRight: 4 }} />{text}
    </div>
  );
}

function Skel({ h }: { h: number }) { return <div className="vac-skel" style={{ height: h, width: "100%" }} />; }
function NoData(): JSX.Element {
  return <div style={{ color: D.axis, fontSize: 12, padding: "22px 0", textAlign: "center" }}>Нет данных за выбранный период</div>;
}
// Пусто И есть caveat — причина уже напечатана под заголовком, своими словами не дублируем.
function Empty({ caveat }: { caveat?: string | null }): JSX.Element | null {
  return caveat ? null : <NoData />;
}
function ErrRetry({ msg, onRetry }: { msg: string; onRetry: () => void }) {
  return (
    <div style={{ color: D.redText, fontSize: 12, padding: "16px 0", textAlign: "center" }}>
      Ошибка загрузки: {msg}
      <div><button className="vac-btn" style={{ marginTop: 8, height: 28 }} onClick={onRetry}>Повторить</button></div>
    </div>
  );
}

// ── разворот виджета на весь экран (⛶ / Esc) ──
/** Переключатель порядка «Лучшие сверху» ⇄ «Худшие сверху».
 *
 *  Вид повторяет кнопку из блока качества медкарт (quality/QualityWidgets.tsx),
 *  чтобы на разных экранах одно и то же действие выглядело одинаково.
 *
 *  ВАЖНО: разворачивать список можно только когда с бэкенда пришли ВСЕ строки.
 *  У рейтинга участков раньше отдавались лишь 50 лучших, и «худшие сверху»
 *  показали бы пятидесятое место вместо настоящих аутсайдеров — обрезка снята.
 */
function SortToggle({ worstFirst, onToggle }: { worstFirst: boolean; onToggle: () => void }) {
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
      <button onClick={onToggle} title="Порядок строк"
        style={{ background: "transparent", color: D.hint, border: `1px solid ${SHELL.border}`,
          borderRadius: 8, padding: "2px 10px", fontSize: 12, cursor: "pointer", lineHeight: 1.6 }}>
        {worstFirst ? "Худшие сверху" : "Лучшие сверху"}
      </button>
    </div>
  );
}

/** Отсортировать по числовому полю: худшие (меньшие) сверху или наоборот.
 *  Пустые значения всегда в конце — «нет данных» это не «плохо». */
function bySort<T>(rows: T[], key: (r: T) => number | null | undefined, worstFirst: boolean): T[] {
  return [...rows].sort((a, b) => {
    const x = key(a), y = key(b);
    const xe = x === null || x === undefined || !Number.isFinite(x);
    const ye = y === null || y === undefined || !Number.isFinite(y);
    if (xe && ye) return 0;
    if (xe) return 1;
    if (ye) return -1;
    return worstFirst ? (x as number) - (y as number) : (y as number) - (x as number);
  });
}

function WidgetShell({ title, stripe, drill, caveat, expandable = true, children }: {
  title: string; stripe: string; drill?: () => void; caveat?: string | null; expandable?: boolean;
  children: (expanded: boolean) => React.ReactNode;
}) {
  const [full, setFull] = useState(false);
  useEffect(() => {
    if (!full) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setFull(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [full]);

  const inner = (expanded: boolean) => (
    <>
      <WTitle stripe={stripe}>{title}</WTitle>
      <Caveat text={caveat} expanded={expanded} />
      {expandable && (
        <button className="vac-exp" title={expanded ? "Свернуть (Esc)" : "На весь экран"}
          onClick={() => setFull(!expanded)} style={expanded ? { fontSize: 18, top: 12, right: 12 } : undefined}>
          <i className={`ti ${expanded ? "ti-x" : "ti-maximize"}`} />
        </button>
      )}
      {drill && (
        <button className="vac-exp" title="Таблица прививок" onClick={drill}
          style={{ right: !expandable ? 9 : expanded ? 44 : 32, top: expanded ? 12 : 9, fontSize: expanded ? 18 : 13 }}>
          <i className="ti ti-table" />
        </button>
      )}
      {expanded ? (
        <div className="vac-scroll" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column",
          justifyContent: "safe center", overflowY: "auto", overflowX: "hidden",
          maxWidth: 1600, width: "100%", margin: "0 auto" }}>
          {children(true)}
        </div>
      ) : children(false)}
    </>
  );

  return (
    <>
      <section className="vac-widget">{inner(false)}</section>
      {full && (
        <>
          <div onClick={() => setFull(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(4,10,24,0.7)", zIndex: 14000 }} />
          <section className="vac-widget vac-full-card"
            style={{ position: "fixed", inset: "4%", width: "92%", height: "92%", zIndex: 14001,
              overflow: "auto", padding: 18, boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
            {inner(true)}
          </section>
        </>
      )}
    </>
  );
}

// ── ECharts: единая обёртка с тёмным фоном ──
function Chart({ option, height, onClick }: { option: any; height: number | string; onClick?: (p: any) => void }) {
  return (
    <ReactECharts
      option={option}
      opts={{ devicePixelRatio: Math.max(2, window.devicePixelRatio || 1) }}
      style={{ height, width: "100%" }}
      onEvents={onClick ? { click: onClick } : undefined}
      notMerge={true}
      lazyUpdate={true}
    />
  );
}
const AXIS_STYLE = {
  axisLine: { lineStyle: { color: W.divider } },
  axisTick: { show: false },
  axisLabel: { color: D.axis, fontSize: 10 },
  splitLine: { lineStyle: { color: W.divider, type: "dashed" } },
};
// ось значений: разряды пробелом (запятые ECharts по умолчанию читаются как дроби)
const VALUE_AXIS = { ...AXIS_STYLE, axisLabel: { color: D.axis, fontSize: 10, formatter: (v: number) => fmt(v) } };
const TOOLTIP = {
  backgroundColor: "#0e2540", borderColor: W.cardBorder, textStyle: { color: SHELL.text, fontSize: 12 },
};

function LegendRow({ color, label, value, valueColor, bold, onClick }: {
  color: string; label: string; value: React.ReactNode; valueColor?: string; bold?: boolean; onClick?: () => void;
}) {
  return (
    <div className={onClick ? "vac-row-drill" : undefined} onClick={onClick}
      style={{ display: "flex", justifyContent: "space-between", gap: 8, borderRadius: 4, padding: "0 2px" }}>
      <span style={{ color: D.label, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        <span style={{ color }}>●</span> {label}
      </span>
      <span style={{ color: valueColor || SHELL.text, fontWeight: bold ? 500 : undefined, whiteSpace: "nowrap" }}>{value}</span>
    </div>
  );
}

function InlineBar({ frac, color, h }: { frac: number; color: string; h: number }) {
  return (
    <div style={{ background: W.track, borderRadius: 2, height: h, width: "100%" }}>
      <div style={{ width: `${Math.max(0, Math.min(100, frac * 100))}%`, background: color, height: h, borderRadius: 2 }} />
    </div>
  );
}

function MiniStat({ value, label, color, hint }: { value: string; label: string; color: string; hint?: string | null }) {
  return (
    <div style={{ background: "#13253d", borderRadius: 5, padding: 5, textAlign: "center" }} title={hint || undefined}>
      <div style={{ fontSize: 14, fontWeight: 500, color }}>{value}</div>
      <div style={{ fontSize: 8, color: D.axis }}>{label}</div>
      {hint && <div style={{ fontSize: 7.5, color: D.hint, lineHeight: 1.25, marginTop: 2,
        overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{hint}</div>}
    </div>
  );
}

function KpiCard({ label, icon, value, sub, tone, caveat, onClick }: {
  label: string; icon: string; value: React.ReactNode; sub: string; tone: ToneKey;
  caveat?: string | null; onClick?: () => void;
}) {
  const t = TONES[tone];
  return (
    <div className={onClick ? "vac-drillable" : undefined} onClick={onClick} title={caveat || undefined}
      style={{ background: t.bg, border: `0.5px solid ${t.border}`, borderRadius: 9, padding: "9px 10px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 9.5, color: t.label, textTransform: "uppercase", letterSpacing: 0.3, fontWeight: 500 }}>{label}</span>
        <i className={`ti ${icon}`} style={{ fontSize: 14, color: t.accent }} />
      </div>
      <div style={{ fontSize: 20, fontWeight: 500, color: "#fff", marginTop: 2 }}>{value}</div>
      <div style={{ fontSize: 9, color: t.sub, marginTop: 1 }}>{sub}</div>
      {caveat && (
        <div style={{ fontSize: 7.5, color: t.sub, marginTop: 3, lineHeight: 1.25, opacity: 0.85,
          overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
          {caveat}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════ СТРАНИЦА ═══════════════════════
export default function VaccinationDashboardPage(): JSX.Element {
  const qs = new URLSearchParams(window.location.search);
  const now = new Date();

  const [misId, setMisId] = useState<string>(qs.get("mis_id") || "");
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [year, setYear] = useState<number>(Number(qs.get("year")) || now.getFullYear());
  const [month, setMonth] = useState<number>(Number(qs.get("month")) || 0);
  const [dateFrom, setDateFrom] = useState<string>(qs.get("date_from") || "");
  const [dateTo, setDateTo] = useState<string>(qs.get("date_to") || "");
  // Порядок строк в ранжируемых виджетах: по умолчанию лучшие сверху,
  // кнопка на каждом виджете переворачивает. Состояние своё у каждого —
  // переключение рейтинга не должно менять порядок в списке мест.
  const [worst, setWorst] = useState<Record<string, boolean>>({});
  const flipKey = (k: string) => setWorst((v) => ({ ...v, [k]: !v[k] }));

  const [flt, setFlt] = useState<{ area?: string; contingent?: string; vac_type?: string;
    prep?: string; doctor?: string; place?: string }>({
    area: qs.get("area") || undefined, contingent: qs.get("contingent") || undefined,
    vac_type: qs.get("vac_type") || undefined, prep: qs.get("prep") || undefined,
    doctor: qs.get("doctor") || undefined,
  });

  const [boot, setBoot] = useState<Bootstrap | null>(null);
  const [sum, setSum] = useState<Summary | null>(null);
  const [wg, setWg] = useState<Widgets | null>(null);
  const [sumErr, setSumErr] = useState("");
  const [wgErr, setWgErr] = useState("");
  const [sumLoading, setSumLoading] = useState(true);
  const [wgLoading, setWgLoading] = useState(true);
  const [imm, setImm] = useState<Immuno | null>(null);
  const [immErr, setImmErr] = useState("");
  const [immLoading, setImmLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const params = useMemo(() => ({
    mis_id: misId, year, month: month || undefined,
    date_from: dateFrom || undefined, date_to: dateTo || undefined,
    area: flt.area, contingent: flt.contingent, vac_type: flt.vac_type, prep: flt.prep,
    doctor: flt.doctor, place: flt.place,
  }), [misId, year, month, dateFrom, dateTo, flt]);

  useEffect(() => {
    fetchOrgs().then((list) => {
      setOrgs(list);
      if (!misId && list.length) setMisId(list[0].id);
    }).catch(() => { /* селектор останется пустым, mis из URL */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!misId) return;
    api<Bootstrap>("/api/v1/vaccination/dashboard/bootstrap", { mis_id: misId }).then(setBoot).catch(() => {});
  }, [misId]);

  useEffect(() => {
    if (!misId) return;
    setSumLoading(true); setSumErr("");
    api<Summary>("/api/v1/vaccination/dashboard/summary", params)
      .then(setSum).catch((e) => setSumErr(String(e?.message || e))).finally(() => setSumLoading(false));
  }, [params, misId, reloadKey]);
  useEffect(() => {
    if (!misId) return;
    setWgLoading(true); setWgErr("");
    api<Widgets>("/api/v1/vaccination/dashboard/widgets", params)
      .then(setWg).catch((e) => setWgErr(String(e?.message || e))).finally(() => setWgLoading(false));
  }, [params, misId, reloadKey]);

  // Приёмы по иммунопрофилактике грузим ОТДЕЛЬНЫМ запросом, а не внутри widgets:
  // источник другой (база дефектолога, поток документов МИС), и считается он
  // дольше. Отдельный запрос не тормозит остальной экран, а если бы упал —
  // остальные девять виджетов остаются на месте.
  useEffect(() => {
    if (!misId) return;
    setImmLoading(true); setImmErr("");
    api<Immuno>("/api/v1/vaccination/dashboard/immuno", params)
      .then(setImm).catch((e) => setImmErr(String(e?.message || e))).finally(() => setImmLoading(false));
  }, [params, misId, reloadKey]);

  // drill-down: текущие фильтры + срез (status / age_group / area / doctor) → новая вкладка
  const openCards = useCallback((extra?: Record<string, string | undefined>) => {
    const p = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") p.set(k, String(v)); });
    Object.entries(extra || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") p.set(k, String(v)); });
    window.open(`${publicBase()}/dashboards/vaccination/cards?${p.toString()}`, "_blank", "noopener,noreferrer");
  }, [params]);

  // Переход в пофамильный список просрочки. Фильтры прививки (контингент, препарат,
  // врач, вид, статус) сюда НЕ передаём: они относятся к сделанной прививке,
  // а у просроченного требования её нет — параметр был бы обманом.
  const openOverdue = useCallback((extra?: Record<string, string | number | undefined>) => {
    const p = new URLSearchParams();
    (["mis_id", "year", "month", "date_from", "date_to", "area"] as const).forEach((k) => {
      const v = (params as any)[k];
      if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
    });
    Object.entries(extra || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") p.set(k, String(v)); });
    window.open(`${publicBase()}/dashboards/vaccination/overdue?${p.toString()}`, "_blank", "noopener,noreferrer");
  }, [params]);

  // Общий открыватель табличных страниц: /cards, /overdue, /exemptions, /areas.
  // Фильтры прививки (контингент, препарат, врач, вид, статус) передаём только
  // туда, где они имеют смысл: у медотвода нет ни препарата, ни вида прививки,
  // а список участков — вообще не про прививки.
  const openTable = useCallback((page: "exemptions" | "areas",
                                 extra?: Record<string, string | number | undefined>) => {
    const p = new URLSearchParams();
    const keep = page === "exemptions"
      ? (["mis_id", "year", "month", "date_from", "date_to", "area"] as const)
      : (["mis_id", "year", "month", "date_from", "date_to"] as const);
    keep.forEach((k) => {
      const v = (params as any)[k];
      if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
    });
    Object.entries(extra || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") p.set(k, String(v)); });
    window.open(`${publicBase()}/dashboards/vaccination/${page}?${p.toString()}`, "_blank", "noopener,noreferrer");
  }, [params]);

  // Таблица приёмов по иммунопрофилактике. Фильтры «вид прививки» и «препарат»
  // сюда НЕ передаём: у протокола осмотра нет ни вида, ни препарата — параметр
  // был бы обманом, ровно как у медотводов.
  const openImmuno = useCallback((extra?: Record<string, string | number | undefined>) => {
    const p = new URLSearchParams();
    (["mis_id", "year", "month", "date_from", "date_to", "area", "contingent",
      "doctor", "place"] as const).forEach((k) => {
      const v = (params as any)[k];
      if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
    });
    Object.entries(extra || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") p.set(k, String(v)); });
    window.open(`${publicBase()}/dashboards/vaccination/immuno?${p.toString()}`, "_blank", "noopener,noreferrer");
  }, [params]);

  const resetAll = () => {
    setMonth(0); setDateFrom(""); setDateTo(""); setFlt({}); setYear(now.getFullYear());
  };

  const c = sum?.cards;
  const years = boot?.years?.length ? boot.years : [0, 1, 2, 3].map((d) => now.getFullYear() - d);

  return (
    <div style={{ minHeight: "100vh", background: SHELL.page, padding: 10, color: SHELL.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <GlobalCss />
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>

        {/* ── ШАПКА + ПАНЕЛЬ ФИЛЬТРОВ ── */}
        <div style={{ background: SHELL.card, borderRadius: 12, border: `1px solid ${SHELL.border}`, padding: 12, marginBottom: 10 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "nowrap", minWidth: 0 }}>
            <select className="vac-select" title="Подразделение" value={misId} onChange={(e) => setMisId(e.target.value)}>
              {!orgs.length && <option value={misId}>{misId || "—"}</option>}
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>

            <select className="vac-select" style={{ width: 76, flex: "0 0 76px" }} title="Год" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <select className="vac-select" style={{ width: 96, flex: "0 0 96px" }} title="Месяц" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              <option value={0}>Месяц: весь год</option>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>

            <PeriodCalendar
              dateFrom={dateFrom || undefined}
              dateTo={dateTo || undefined}
              onApply={(from, to) => { setDateFrom(from); setDateTo(to); }}
              onClear={() => { setDateFrom(""); setDateTo(""); }}
            />

            <select className="vac-select" style={{ width: 112, flex: "0 0 112px" }} title="Участок" value={flt.area || ""} onChange={(e) => setFlt({ ...flt, area: e.target.value || undefined })}>
              <option value="">Участок</option>
              {(boot?.areas || []).map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <select className="vac-select" style={{ width: 116, flex: "0 0 116px" }} title="Контингент" value={flt.contingent || ""} onChange={(e) => setFlt({ ...flt, contingent: e.target.value || undefined })}>
              <option value="">Контингент</option>
              {(boot?.contingents || []).map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            <select className="vac-select" style={{ width: 104, flex: "0 0 104px" }} title="Вид прививки" value={flt.vac_type || ""} onChange={(e) => setFlt({ ...flt, vac_type: e.target.value || undefined })}>
              <option value="">Вид</option>
              {(boot?.vac_types || []).map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            <select className="vac-select" style={{ width: 108, flex: "0 0 108px" }} title="Препарат" value={flt.prep || ""} onChange={(e) => setFlt({ ...flt, prep: e.target.value || undefined })}>
              <option value="">Препарат</option>
              {(boot?.preparations || []).map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            {/* Место вакцинации — подразделение врача. Как и препарат, это свойство
                прививки, а не человека: план и охват по нему не считаются. */}
            <select className="vac-select" style={{ width: 108, flex: "0 0 108px" }} title="Место вакцинации" value={flt.place || ""} onChange={(e) => setFlt({ ...flt, place: e.target.value || undefined })}>
              <option value="">Место</option>
              {(boot?.places || []).map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            <select className="vac-select" style={{ width: 96, flex: "0 0 96px" }} title="Врач" value={flt.doctor || ""} onChange={(e) => setFlt({ ...flt, doctor: e.target.value || undefined })}>
              <option value="">Врачи</option>
              {(boot?.doctors || []).map((d) => <option key={d.post_id} value={d.post_id}>{d.fio}</option>)}
            </select>

            <button className="vac-btn" style={{ padding: 0, width: 34, minWidth: 34, flex: "0 0 34px" }} title="Сбросить все фильтры" onClick={resetAll}><i className="ti ti-filter-off" /></button>
            <button className="vac-btn" style={{ padding: 0, width: 34, minWidth: 34, flex: "0 0 34px" }} title="Обновить данные" onClick={() => setReloadKey((k) => k + 1)}><i className="ti ti-refresh" /></button>

            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
              <button className="vac-btn" style={{ padding: "0 10px" }} title="Назад: Диспансерный учёт"
                onClick={() => {
                  const mis = (misId || "").trim();
                  window.location.href = "/dashboards/duchet" + (mis ? "?mis_id=" + encodeURIComponent(mis) : "");
                }}>
                {"<<"}
              </button>
              <button className="vac-btn" style={{ padding: "0 10px", opacity: 0.45, cursor: "default" }} disabled
                title="Это последняя закладка">
                {">>"}
              </button>
              <details style={{ position: "relative" }}>
                <summary style={{ color: "#fff", cursor: "pointer", listStyle: "none", fontSize: 18, padding: "0 4px" }}>☰</summary>
                <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", background: SHELL.page,
                  border: `1px solid ${SHELL.border}`, borderRadius: 8, padding: 8, display: "flex",
                  flexDirection: "column", gap: 6, zIndex: 20000, minWidth: 220 }}>
                  {[
                    { label: "Главная", href: "/" },
                    { label: "Dashboards АПП", href: "/dashboards/app" },
                    { label: "Dashboards КС", href: "/dashboards/hospital?place=%D0%9A%D0%A1" },
                    { label: "Охрана материнства", href: "/dashboards/mother-hospital" },
                    { label: "Dashboards ДС", href: "/dashboards/hospital?place=%D0%94%D0%A1" },
                    { label: "Ситуационный центр", href: "/dashboards/situation-center" },
                    { label: "Диспансерный учёт", href: "/dashboards/duchet" },
                    { label: "Вакцинация", href: "/dashboards/vaccination" },
                  ].map((route) => (
                    <a key={route.href} href={route.href} target="_blank" rel="noreferrer"
                      style={{ color: "#fff", padding: "6px 8px", borderRadius: 6, textDecoration: "none" }}>
                      {route.label}
                    </a>
                  ))}
                </div>
              </details>
            </div>
          </div>
        </div>

        {/* ═══ ПАНЕЛЬ-«РАЗВОРОТ» ═══ */}
        <div style={{ background: W.wrap, borderRadius: 14, padding: 13 }}>

          {/* ── ВЕРХНЯЯ ПАНЕЛЬ: 6 карточек ── */}
          {sumErr ? (
            <div style={{ marginBottom: 9 }}><ErrRetry msg={sumErr} onRetry={() => setReloadKey((k) => k + 1)} /></div>
          ) : (
            <div className="vac-kpi">
              {sumLoading || !c ? (
                Array.from({ length: 6 }, (_, i) => <Skel key={i} h={68} />)
              ) : (
                <>
                  <KpiCard label="Проведено вакцинаций" icon="ti-vaccine" tone="green"
                    value={<span style={{ color: D.greenText }}>{fmt(c.done.today)}</span>}
                    sub={`сегодня · за неделю ${fmt(c.done.week)} · за период ${fmt(c.done.period)}`}
                    caveat={c.done.caveat}
                    onClick={() => openCards({ status: "Проведена" })} />
                  <KpiCard label="Выполнение плана" icon="ti-target-arrow"
                    tone={c.plan.pct !== null && c.plan.pct >= 90 ? "green" : c.plan.pct !== null && c.plan.pct >= 70 ? "yellow" : "red"}
                    value={fmtPct(c.plan.pct)}
                    sub={`план ${fmt(c.plan.plan)} · факт ${fmt(c.plan.fact)}`}
                    caveat={c.plan.caveat}
                    onClick={() => openCards({ status: "Проведена" })} />
                  {/* Цвет районной цифре не даём: сравнивать её не с чем — утверждённой
                      целевой цифры у заказчика нет, а красить относительно самой себя
                      бессмысленно. Цвет остаётся у участков, там ориентир — район. */}
                  <KpiCard label="Охват населения" icon="ti-chart-pie" tone="blue"
                    value={fmtPct(c.coverage.pct)}
                    sub={`привиты полностью ${fmt(c.coverage.full)} из ${fmt(c.coverage.due)}`}
                    caveat={c.coverage.caveat}
                    onClick={() => openCards({ status: "Проведена" })} />
                  <KpiCard label="Просроченные" icon="ti-clock-exclamation" tone="red"
                    value={<span style={{ color: D.redText }}>{fmt(c.overdue.total)}</span>}
                    sub={`детей ${fmt(c.overdue.children)} · взрослых ${fmt(c.overdue.adults)}`}
                    caveat={c.overdue.caveat}
                    onClick={() => openCards({ status: "НеПроведена" })} />
                  <KpiCard label="Отказы от вакцинации" icon="ti-user-off" tone="violet"
                    value={<span style={{ color: D.violetText }}>{fmt(c.refusals.total)}</span>}
                    sub={`${fmtPct(c.refusals.pct_of_invited)} от приглашённых`}
                    caveat={c.refusals.caveat} />
                </>
              )}
            </div>
          )}

          {/* ── СЕТКА ВИДЖЕТОВ: колонки [1,4,7] [2,5,8] [3,6,9] ── */}
          {wgErr ? (
            <ErrRetry msg={wgErr} onRetry={() => setReloadKey((k) => k + 1)} />
          ) : (
            <div className="vac-grid">

              {/* ═ КОЛОНКА 1 ═ */}
              <div className="vac-col">

                {/* 1. Выполнение плана вакцинации */}
                <WidgetShell title="1. Выполнение плана вакцинации" stripe={D.green} caveat={wg?.w1_plan?.caveat}
                  drill={() => openCards({ status: "Проведена" })}>
                  {(exp) => wgLoading || !wg ? <Skel h={190} /> : (() => {
                    const p = wg.w1_plan;
                    const pct = p.pct;
                    const gauge = {
                      backgroundColor: "transparent",
                      series: [{
                        type: "gauge", startAngle: 215, endAngle: -35, min: 0, max: 100, radius: "96%",
                        center: ["50%", "58%"],
                        progress: { show: true, width: exp ? 26 : 15, roundCap: true,
                          itemStyle: { color: planColor(pct) } },
                        axisLine: { lineStyle: { width: exp ? 26 : 15, color: [[1, "#17293f"]] } },
                        pointer: { show: false }, axisTick: { show: false }, splitLine: { show: false },
                        axisLabel: { show: false }, anchor: { show: false },
                        title: { show: true, offsetCenter: [0, "32%"], color: D.axis, fontSize: exp ? 16 : 10 },
                        detail: { offsetCenter: [0, "4%"], color: "#fff", fontSize: exp ? 40 : 22, fontWeight: 500,
                          formatter: () => fmtPct(pct) },
                        data: [{ value: pct === null ? 0 : pct, name: "выполнено" }],
                      }],
                    };
                    return (
                      <>
                        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "nowrap" }}>
                          <div style={{ width: exp ? "46%" : "48%", flex: "0 0 auto" }}>
                            <Chart option={gauge} height={exp ? "44vh" : 150} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0, fontSize: exp ? 20 : 13.5, lineHeight: 2.0 }}>
                            <LegendRow color={D.green} label="Факт" value={fmt(p.fact)} valueColor={D.greenText}
                              onClick={() => openCards({ status: "Проведена" })} />
                            <LegendRow color={D.blue} label="План" value={fmt(p.plan)} />
                            <LegendRow color={D.gray} label="Осталось" value={fmt(p.left)} />
                          </div>
                        </div>
                        <div style={{ borderTop: `0.5px solid ${W.divider}`, marginTop: 8, paddingTop: 7 }}>
                          <div style={{ fontSize: exp ? 15 : 11, color: D.axis, marginBottom: 3 }}>Динамика по неделям</div>
                          {!p.dynamics?.length ? <Empty caveat={p.caveat} /> : (
                            <Chart height={exp ? "20vh" : 74} option={{
                              backgroundColor: "transparent",
                              grid: { left: 38, right: 10, top: 8, bottom: 20 },
                              tooltip: { trigger: "axis", ...TOOLTIP },
                              xAxis: { type: "category", data: p.dynamics.map((d) => shortDate(d.week)), ...AXIS_STYLE, splitLine: { show: false } },
                              yAxis: { type: "value", ...VALUE_AXIS, splitNumber: exp ? 5 : 2 },
                              series: [{
                                type: "line", smooth: true, showSymbol: false, name: "проведено",
                                data: p.dynamics.map((d) => d.done),
                                lineStyle: { color: D.green, width: 2 }, itemStyle: { color: D.green },
                                areaStyle: { color: "rgba(37,194,116,0.22)" },
                              }],
                            }} />
                          )}
                        </div>
                      </>
                    );
                  })()}
                </WidgetShell>

                {/* 4. Просроченные вакцинации */}
                {/* Переходы ведут в ПОФАМИЛЬНЫЙ СПИСОК, а не в таблицу прививок.
                    Раньше здесь стоял openCards({status:"НеПроведена"}) — это 194 отказа
                    на приёме, тогда как виджет показывает 44 704 просроченных требования:
                    клик открывал таблицу, не имеющую отношения к показанной цифре. */}
                <WidgetShell title="4. Просроченные вакцинации" stripe={D.red} caveat={wg?.w4_overdue?.caveat}
                  drill={() => openOverdue({ plan_year: year })}>
                  {(exp) => wgLoading || !wg ? <Skel h={150} /> : !wg.w4_overdue.groups.length ? <Empty caveat={wg.w4_overdue.caveat} /> : (() => {
                    // Хуже = БОЛЬШЕ просроченных требований, поэтому направление
                    // обратное: «худшие сверху» это по убыванию.
                    const gs = bySort(wg.w4_overdue.groups, (g) => g.n, !worst.overdue);
                    const colors = [D.red, D.orange, D.yellow, D.blue, D.violet];
                    const option = {
                      backgroundColor: "transparent",
                      tooltip: { trigger: "item", ...TOOLTIP, formatter: (x: any) => `${x.name}: ${fmt(x.value)} (${x.percent}%)` },
                      series: [{
                        type: "pie", radius: ["56%", "86%"], center: ["50%", "50%"], avoidLabelOverlap: false,
                        label: { show: true, position: "center", formatter: () => fmt(wg.w4_overdue.total),
                          color: "#fff", fontSize: exp ? 34 : 18, fontWeight: 500 },
                        emphasis: { label: { show: true } },
                        labelLine: { show: false },
                        itemStyle: { borderColor: W.card, borderWidth: 2 },
                        data: gs.map((g, i) => ({ name: g.group, value: g.n, itemStyle: { color: colors[i % colors.length] } })),
                      }],
                    };
                    return (
                      // Кольцо занимает почти всю высоту виджета: раньше стояло 140 px
                      // и в растянутом контейнере болталось в верхней половине.
                      <>
                        <SortToggle worstFirst={!worst.overdue} onToggle={() => flipKey("overdue")} />
                      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "nowrap",
                        height: "100%", minHeight: exp ? undefined : 200 }}>
                        <div style={{ width: exp ? "48%" : "46%", flex: "0 0 auto", height: "100%" }}>
                          <Chart option={option} height={exp ? "50vh" : "100%"}
                            onClick={(x: any) => openOverdue({ plan_year: year, group: String(x?.name || "") })} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0, fontSize: exp ? 20 : 13, lineHeight: 1.95 }}>
                          {gs.map((g, i) => (
                            <LegendRow key={g.group} color={colors[i % colors.length]} label={g.group} value={fmt(g.n)}
                              onClick={() => openOverdue({ plan_year: year, group: g.group })} />
                          ))}
                        </div>
                      </div>
                      </>
                    );
                  })()}
                </WidgetShell>

                {/* 6. Отказы от вакцинации */}
                <WidgetShell title="6. Отказы от вакцинации" stripe={D.violet} caveat={wg?.w6_refusals?.caveat}
                  drill={() => openTable("exemptions", {})}>
                  {(exp) => wgLoading || !wg ? <Skel h={160} /> : !wg.w6_refusals.reasons.length ? <Empty caveat={wg.w6_refusals.caveat} /> : (() => {
                    // Хуже = БОЛЬШЕ отказов, направление обратное.
                    const rs = bySort(wg.w6_refusals.reasons, (r) => r.n, !worst.refusals);
                    const colors = [D.violet, D.blue, D.green, D.yellow, D.orange, D.gray, D.grayDim, D.blue3];
                    const shortReason = (s: string) => (s.length > 42 ? s.slice(0, 40) + "…" : s);
                    const donut = {
                      backgroundColor: "transparent",
                      tooltip: { trigger: "item", ...TOOLTIP, formatter: (x: any) => `${x.name}: ${fmt(x.value)} (${x.percent}%)` },
                      series: [{
                        type: "pie", radius: ["56%", "80%"], center: ["50%", "50%"],
                        label: { show: true, position: "center", formatter: () => fmt(wg.w6_refusals.total),
                          color: "#fff", fontSize: exp ? 32 : 17, fontWeight: 500 },
                        emphasis: { label: { show: true } }, labelLine: { show: false },
                        itemStyle: { borderColor: W.card, borderWidth: 2 },
                        data: rs.map((r, i) => ({ name: r.reason, value: r.n, itemStyle: { color: colors[i % colors.length] } })),
                      }],
                    };
                    const rows = exp ? rs : rs.slice(0, 5);
                    return (
                      <>
                        <SortToggle worstFirst={!worst.refusals} onToggle={() => flipKey("refusals")} />
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div style={{ width: exp ? "42%" : "44%", flex: "0 0 auto" }}>
                            <Chart option={donut} height={exp ? "34vh" : 130} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0, fontSize: exp ? 15 : 10, lineHeight: 1.7 }}>
                            {rows.map((r, i) => (
                              <div key={r.reason} title={r.reason}>
                                <LegendRow color={colors[i % colors.length]} label={shortReason(r.reason)} value={fmt(r.n)} />
                              </div>
                            ))}
                          </div>
                        </div>
                        <div style={{ borderTop: `0.5px solid ${W.divider}`, marginTop: 8, paddingTop: 7 }}>
                          <div style={{ fontSize: exp ? 15 : 11, color: D.axis, marginBottom: 3 }}>Динамика по месяцам</div>
                          {!wg.w6_refusals.dynamics.length ? <Empty caveat={wg.w6_refusals.caveat} /> : (
                            <Chart height={exp ? "18vh" : 72} option={{
                              backgroundColor: "transparent",
                              grid: { left: 34, right: 10, top: 8, bottom: 20 },
                              tooltip: { trigger: "axis", ...TOOLTIP },
                              xAxis: { type: "category", data: wg.w6_refusals.dynamics.map((d) => monthLabel(d.month)), ...AXIS_STYLE, splitLine: { show: false } },
                              yAxis: { type: "value", ...VALUE_AXIS, splitNumber: exp ? 5 : 2 },
                              series: [{
                                type: "line", smooth: true, showSymbol: false, name: "отказы",
                                data: wg.w6_refusals.dynamics.map((d) => d.n),
                                lineStyle: { color: D.violet, width: 2 }, itemStyle: { color: D.violet },
                                areaStyle: { color: "rgba(132,121,230,0.22)" },
                              }],
                            }} />
                          )}
                        </div>
                      </>
                    );
                  })()}
                </WidgetShell>
              </div>

              {/* ═ КОЛОНКА 2 ═ */}
              <div className="vac-col">

                {/* 2. Охват вакцинацией по возрастным группам */}
                <WidgetShell title="2. Охват вакцинацией по возрастным группам" stripe={D.blue} caveat={wg?.w2_age_groups?.caveat}>
                  {(exp) => wgLoading || !wg ? <Skel h={170} /> : !wg.w2_age_groups.groups.length ? <Empty caveat={wg.w2_age_groups.caveat} /> : (() => {
                    // Хуже = ниже охват. У профессиональных контингентов охвата нет
                    // (знаменателя не существует) — такие строки всегда в конце.
                    const gs = bySort(wg.w2_age_groups.groups, (g) => g.pct, !!worst.cont);
                    const option = {
                      backgroundColor: "transparent",
                      grid: { left: 46, right: 12, top: 26, bottom: exp ? 40 : 62 },
                      tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, ...TOOLTIP,
                        formatter: (ps: any) => {
                          const i = ps?.[0]?.dataIndex ?? 0;
                          const g = gs[i];
                          const rows = (ps || []).map((x: any) =>
                            `${x.marker} ${x.seriesName}: ${fmt(x.value)}`).join("<br/>");
                          const pct = g?.pct === null || g?.pct === undefined ? "" :
                            `<br/>Охват: ${fmtPct(g.pct)}`;
                          return `<b>${g?.group ?? ""}</b><br/>${rows}${pct}`;
                        } },
                      legend: { data: ["План", "Факт"], textStyle: { color: D.axis, fontSize: exp ? 13 : 9 },
                        top: 0, itemWidth: 10, itemHeight: 8 },
                      // На оси — короткие подписи: полные названия контингентов доходят до
                      // 69 символов и наезжают друг на друга. Полное имя — в подсказке.
                      xAxis: { type: "category", data: gs.map((g) => g.short || g.group), ...AXIS_STYLE,
                        splitLine: { show: false },
                        axisLabel: { color: D.axis, fontSize: exp ? 12 : 8, interval: 0,
                          rotate: exp ? 0 : 32, hideOverlap: false,
                          formatter: (v: string) => String(v).replace(" ", "\n") } },
                      // Логарифмическая шкала: разброс от 86 709 до 1, на линейной шкале
                      // всё кроме двух-трёх столбцов сливается с осью. Логарифм не
                      // принимает 0 и отрицательные — нули отдаём как null, ECharts их
                      // пропускает (иначе шкала ломается и график пропадает целиком).
                      yAxis: { type: "log", logBase: 10, min: 1, ...VALUE_AXIS },
                      series: [
                        { name: "План", type: "bar", barGap: "8%", itemStyle: { color: D.blue4, borderRadius: [2, 2, 0, 0] },
                          data: gs.map((g) => (!g.plan ? null : g.plan)) },
                        { name: "Факт", type: "bar", itemStyle: { color: D.green, borderRadius: [2, 2, 0, 0] },
                          data: gs.map((g) => (!g.fact ? null : g.fact)) },
                      ],
                    };
                    return (
                      <>
                        <SortToggle worstFirst={!!worst.cont} onToggle={() => flipKey("cont")} />
                        <Chart option={option} height={exp ? "50vh" : 176}
                          onClick={(x: any) => {
                            const g = gs[x?.dataIndex ?? -1];
                            openCards({ contingent: g?.group || String(x?.name || ""), status: "Проведена" });
                          }} />
                        <div style={{ borderTop: `0.5px solid ${W.divider}`, marginTop: 8, paddingTop: 7,
                          display: "flex", justifyContent: "space-between", fontSize: exp ? 16 : 11.5 }}>
                          <span style={{ color: D.label }}>Итоговый охват</span>
                          <span style={{ color: covColor(wg.w2_age_groups.total_pct), fontWeight: 500 }}>
                            {fmtPct(wg.w2_age_groups.total_pct)}
                          </span>
                        </div>
                      </>
                    );
                  })()}
                </WidgetShell>

                {/* 5. Рейтинг амбулаторий по охвату */}
                <WidgetShell title="5. Рейтинг амбулаторий по охвату" stripe={D.green}
                  caveat={[wg?.w5_rating?.caveat, wg?.w5_rating?.plan_caveat]
                            .filter(Boolean).join(" · ") || undefined}>
                  {(exp) => wgLoading || !wg ? <Skel h={170} /> : !wg.w5_rating.rows.length ? <Empty caveat={wg.w5_rating.caveat} /> : (() => {
                    const ordered = bySort(wg.w5_rating.rows, (r) => r.coverage, !!worst.rating);
                    const rows = exp ? ordered : ordered.slice(0, 9);
                    const maxCov = Math.max(...wg.w5_rating.rows.map((r) => r.coverage ?? 0), 1);
                    return (
                      <>
                        <SortToggle worstFirst={!!worst.rating} onToggle={() => flipKey("rating")} />
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: exp ? 16 : 11.5 }}>
                          <thead>
                            <tr style={{ color: "#5a6d84", fontSize: exp ? 12 : 8.5, textAlign: "left" }}>
                              <td style={{ paddingBottom: 3 }}>№</td>
                              <td style={{ paddingBottom: 3 }}>Амбулатория</td>
                              <td />
                              <td style={{ textAlign: "right" }}>Охват</td>
                              <td style={{ textAlign: "right" }}>План</td>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r, i) => {
                              const cc = covColor(r.coverage, sum?.cards?.coverage?.pct);
                              return (
                                <tr key={r.name} className="vac-row-drill" onClick={() => openCards({ area: r.name })}
                                  style={{ borderTop: `0.5px solid ${W.rowBorder}` }}>
                                  <td style={{ width: 18, padding: "3px 0", color: D.hint }}>{i + 1}</td>
                                  <td style={{ color: D.rowText, padding: "3px 4px", maxWidth: exp ? 380 : 120,
                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.name}>{r.name}</td>
                                  <td style={{ width: exp ? 300 : 74, padding: "5px 4px" }}>
                                    <InlineBar frac={(r.coverage ?? 0) / maxCov} color={cc} h={8} />
                                  </td>
                                  <td style={{ textAlign: "right", color: cc, fontWeight: 500, padding: "3px 0", whiteSpace: "nowrap" }}>
                                    {fmtPct(r.coverage)}
                                  </td>
                                  <td style={{ textAlign: "right", color: D.axisVal, padding: "3px 0 3px 8px", whiteSpace: "nowrap" }}>
                                    {fmtPct(r.plan_pct ?? null)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <div style={{ fontSize: 10.5, color: D.hint, marginTop: 7 }}>
                          <i className="ti ti-click" style={{ fontSize: 10 }} /> клик по строке → прививки участка
                        </div>
                      </>
                    );
                  })()}
                </WidgetShell>

                {/* 7. Работа сотрудников */}
                <WidgetShell title="7. Работа сотрудников" stripe={D.blue4} caveat={wg?.w7_staff?.caveat}>
                  {(exp) => wgLoading || !wg ? <Skel h={170} /> : (() => {
                    const s = wg.w7_staff;
                    const barOption = (list: { fio: string; n: number }[], color: string) => ({
                      backgroundColor: "transparent",
                      grid: { left: 4, right: 44, top: 4, bottom: 4, containLabel: true },
                      tooltip: { trigger: "item", ...TOOLTIP },
                      xAxis: { type: "value", show: false },
                      // triggerEvent делает подписи оси кликабельными: по умолчанию ECharts
                      // ловит клик только по столбцу, и нажать на фамилию было нельзя.
                      yAxis: { type: "category", inverse: true, data: list.map((d) => d.fio),
                        triggerEvent: true,
                        axisLine: { show: false }, axisTick: { show: false },
                        axisLabel: { color: D.rowText, fontSize: exp ? 13 : 9,
                          // подпись выглядит нажимаемой, иначе неочевидно, что по ФИО можно кликнуть
                          cursor: "pointer",
                          formatter: (v: string) => (v.length > (exp ? 40 : 18) ? v.slice(0, exp ? 38 : 16) + "…" : v) } },
                      series: [{
                        type: "bar", data: list.map((d) => d.n), barWidth: exp ? 18 : 10,
                        itemStyle: { color, borderRadius: [0, 3, 3, 0] },
                        label: { show: true, position: "right", color: D.axisVal, fontSize: exp ? 12 : 9,
                          formatter: (x: any) => fmt(x.value) },
                      }],
                    });
                    const clickTo = (list: { post_id: string; fio: string }[]) => (x: any) => {
                      // Клик по столбцу приходит с dataIndex, клик по подписи оси — без
                      // него, но с именем в value. Обрабатываем оба случая.
                      let row: { post_id: string; fio: string } | undefined =
                        list[Number(x?.dataIndex)];
                      if (!row && x?.targetType === "axisLabel") {
                        const name = String(x?.value ?? "");
                        row = list.find((d) => d.fio === name)
                           || list.find((d) => name.endsWith("…") && d.fio.startsWith(name.slice(0, -1)));
                      }
                      if (row) openCards({ doctor: row.post_id, status: "Проведена" });
                    };
                    return (
                      <>
                        <div style={{ fontSize: exp ? 13 : 9.5, color: D.axis, marginBottom: 2 }}>
                          Врачи ({fmt(s.doctors.length)})
                        </div>
                        <SortToggle worstFirst={!!worst.staff} onToggle={() => flipKey("staff")} />
                        {!s.doctors.length ? <Empty caveat={s.caveat} /> : (
                          // Показываем ВСЕХ врачей со скроллом, а не первую пятёрку.
                          // Высота графика растёт с числом строк, контейнер прокручивается.
                          <div className="vac-scroll-thin" style={{ overflowY: "auto",
                            maxHeight: exp ? "60vh" : 168, paddingRight: 4 }}>
                            <Chart option={barOption(bySort(s.doctors, (d) => d.n, !!worst.staff), D.blue)}
                              height={Math.max(exp ? 300 : 140, s.doctors.length * (exp ? 26 : 20))}
                              onClick={clickTo(s.doctors)} />
                          </div>
                        )}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 5, marginTop: 8,
                          borderTop: `0.5px solid ${W.divider}`, paddingTop: 7 }}>
                          <MiniStat value={fmtNum(s.avg_per_day)} label="вакцинаций в день" color={D.blue} />
                        </div>
                      </>
                    );
                  })()}
                </WidgetShell>
              </div>

              {/* ═ КОЛОНКА 3 ═ */}
              <div className="vac-col">

                {/* 3. Карта района — охват по амбулаториям */}
                <WidgetShell title="3. Карта района — охват по амбулаториям" stripe={D.blue2}
                  caveat={[wg?.w3_map?.caveat,
                           wg?.w3_map?.unmapped?.people
                             ? `вне карты: ${wg.w3_map.unmapped.people.toLocaleString("ru-RU")} человек`
                               + ` на ${wg.w3_map.unmapped.areas} участках`
                             : null].filter(Boolean).join(" · ") || undefined}>
                  {(exp) => wgLoading || !wg ? <Skel h={230} /> : (
                    <>
                      <VacMapWidget rows={wg.w3_map.facilities || []} expanded={exp} showList={exp}
                        benchmark={sum?.cards?.coverage?.pct ?? null}
                        onZoneClick={(facility) => openCards({ facility })} />
                      {/* Легенда обязана повторять colBy() из VacMapWidget: цвет зоны
                          относительный, а не абсолютный. Старые пороги (>95 / 80–95 /
                          <80) остались тут после правки 12.08 и вводили в заблуждение —
                          при охвате района 33% под «менее 80%» попадали ВСЕ зоны. */}
                      {(() => {
                        const bm = sum?.cards?.coverage?.pct ?? null;
                        const hi = bm !== null ? bm * 1.15 : null;
                        const lo = bm !== null ? bm * 0.85 : null;
                        return (
                          <div style={{ display: "flex", gap: 14, marginTop: 6, flexWrap: "wrap",
                            fontSize: exp ? 12 : 9.5, color: D.label }}>
                            <span><span style={{ color: D.green }}>■</span>{" "}
                              выше среднего{hi !== null ? ` (от ${fmtPct(hi)})` : ""}</span>
                            <span><span style={{ color: D.yellow }}>■</span>{" "}
                              как район{bm !== null ? ` (${fmtPct(lo)}–${fmtPct(hi)})` : ""}</span>
                            <span><span style={{ color: D.red }}>■</span>{" "}
                              ниже среднего{lo !== null ? ` (до ${fmtPct(lo)})` : ""}</span>
                            <span><span style={{ color: D.hint }}>■</span> нет данных</span>
                            {bm !== null && <span style={{ color: D.hint }}>средний по району {fmtPct(bm)}</span>}
                          </div>
                        );
                      })()}
                    </>
                  )}
                </WidgetShell>

                {/* 9. Место вакцинации */}
                {/* Отдельного поля «место» МИС не присылает — восстановлено по
                    подразделению врача. Клик по строке открывает прививки этого места. */}
                <WidgetShell title="9. Место вакцинации" stripe={D.blue}
                  caveat={wg?.w9_place?.caveat}>
                  {(exp) => wgLoading || !wg?.w9_place ? <Skel h={170} />
                    : !wg.w9_place.places.length ? <Empty caveat={wg.w9_place.caveat} /> : (() => {
                    const all = bySort(wg.w9_place.places, (x) => x.done, !!worst.place);
                    const rows = exp ? all : all.slice(0, 9);
                    const max = Math.max(...wg.w9_place.places.map((x) => x.done), 1);
                    return (
                      <div style={{ fontSize: exp ? 15 : 11.5 }}>
                        <SortToggle worstFirst={!!worst.place} onToggle={() => flipKey("place")} />
                        {rows.map((r) => (
                          <div key={r.place} className="vac-row-drill"
                            onClick={() => {
                              // status и no_probes обязательны: без них в таблице окажется
                              // больше строк, чем показывает виджет. По «Отделению
                              // физиологии новорожденных» виджет даёт 6 175, а таблица
                              // без этих двух — 6 367: сверху 179 «НеПроведена» и 13 проб.
                              openCards({ place: r.place, status: "Проведена", no_probes: "true" });
                            }}
                            title={`${r.place} — открыть прививки этого места`}
                            style={{ display: "flex", alignItems: "center", gap: 8,
                              padding: exp ? "5px 4px" : "2px 4px", borderRadius: 4 }}>
                            <span style={{ flex: "0 0 auto", width: exp ? 300 : 168,
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {r.place}
                            </span>
                            <span style={{ flex: 1, minWidth: 0 }}>
                              <InlineBar frac={r.done / max} color={D.blue} h={exp ? 10 : 7} />
                            </span>
                            <span style={{ flex: "0 0 auto", width: exp ? 76 : 52, textAlign: "right",
                              fontWeight: 600 }}>{fmt(r.done)}</span>
                            <span style={{ flex: "0 0 auto", width: exp ? 60 : 42, textAlign: "right",
                              color: D.hint }}>{fmtPct(r.pct)}</span>
                          </div>
                        ))}
                        {!exp && all.length > rows.length && (
                          <div style={{ color: D.hint, fontSize: 10.5, marginTop: 3 }}>
                            и ещё {all.length - rows.length} — разверните виджет
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </WidgetShell>

                {/* 8. Критические индикаторы */}
                <WidgetShell title="8. Критические индикаторы" stripe={D.red}>
                  {(exp) => wgLoading || !wg ? <Skel h={130} /> : (() => {
                    const k = wg.w8_critical;
                    const cells: { v: string; label: string; bg: string; border: string; num: string; sub: string;
                      hint?: string | null; drill?: () => void }[] = [
                      { v: fmt(k.overdue_7d), label: "просрочено за 7 дн.", bg: "#361718", border: "#a3302c",
                        num: D.redText, sub: "#c98a88",
                        hint: k.overdue_7d === null ? k.overdue_7d_caveat
                              : `у ${fmt(k.overdue_7d_people)} человек · нажмите для списка`,
                        // Ведём в ПОФАМИЛЬНЫЙ СПИСОК, а не в таблицу прививок: там записи
                        // о сделанном, а здесь нужны люди, у которых прививки нет.
                        drill: () => openOverdue({ days: 7 }) },
                      { v: fmt(k.children_no_vac), label: "детей без прививок", bg: "#361718", border: "#a3302c",
                        num: D.redText, sub: "#c98a88",
                        hint: k.children_no_vac_of
                              ? `из ${fmt(k.children_no_vac_of)}, у кого срок наступил · нажмите для списка`
                              : k.children_no_vac_caveat,
                        drill: () => openOverdue({ kind: "none", age_from: 0, age_to: 17 }) },
                      // Порог «<80%» заменён на «ниже среднего по району»: при охвате 33,6%
                      // под старый порог попадали 328 участков из 331, то есть все.
                      { v: (k.low_coverage_of ?? 0) < 2 ? "—" : fmt(k.low_coverage_areas),
                        label: "участков ниже среднего", bg: "#33280c", border: "#97710f",
                        num: D.yellow, sub: "#bd9a54",
                        hint: (k.low_coverage_of ?? 0) < 2
                              ? "сравнивать не с чем: в выбранном срезе один участок"
                              : k.district_pct !== null && k.district_pct !== undefined
                              ? `из ${fmt(k.low_coverage_of)}; средний по району ${fmtPct(k.district_pct)}`
                                + " · нажмите для списка"
                              : k.low_coverage_caveat,
                        // Плитка про УЧАСТКИ, а не про людей — ведём в список участков
                        drill: (k.low_coverage_of ?? 0) < 2 ? undefined
                               : () => openTable("areas", { below: "true" }) },
                      { v: fmt(k.refusals), label: "отказов (медотводы)", bg: "#221d40", border: "#5b51bd",
                        num: D.violetText, sub: "#8b83cc",
                        hint: "нажмите для списка",
                        drill: () => openTable("exemptions", {}) },
                      { v: fmtPct(k.visit_pct), label: "подтв. визитов", bg: "#0e2540", border: "#2f6fb0",
                        num: D.blue, sub: "#7ba3cd", hint: k.visit_pct === null ? k.visit_caveat : null },
                    ];
                    return (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 5 }}>
                        {cells.map((cell) => (
                          <div key={cell.label} className={cell.drill ? "vac-drillable" : undefined} onClick={cell.drill}
                            title={cell.hint || undefined}
                            style={{ background: cell.bg, border: `0.5px solid ${cell.border}`, borderRadius: 6, padding: exp ? 14 : 6 }}>
                            <div style={{ fontSize: exp ? 26 : 15, fontWeight: 500, color: cell.num }}>{cell.v}</div>
                            <div style={{ fontSize: exp ? 12 : 8, color: cell.sub }}>{cell.label}</div>
                            {cell.hint && (
                              <div style={{ fontSize: exp ? 12 : 7.5, color: D.hint, lineHeight: 1.3, marginTop: 3,
                                overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical" }}>
                                {cell.hint}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </WidgetShell>
              </div>
            </div>
          )}

          {/* ── 10. ПРИЁМЫ ПО ИММУНОПРОФИЛАКТИКЕ — отдельный ряд во всю ширину ──
              Источник другой, чем у виджетов 1–9: протоколы осмотра из МИС
              (повод обращения «Иммунопрофилактика») из базы дефектолога, а не
              выгрузка прививок. Поэтому и ряд отдельный, и запрос отдельный. */}
          {/* HTTP 404 значит, что бэкенд ещё не перезапущен с этой ручкой: фронт
              статический и обновляется раньше API. В этом случае ряд просто
              не показываем — ошибка на экране пользователю ничего не объясняет. */}
          {immErr.includes("404") ? null : immErr ? (
            <div style={{ marginTop: 9 }}>
              <ErrRetry msg={immErr} onRetry={() => setReloadKey((k) => k + 1)} />
            </div>
          ) : (
            <div style={{ marginTop: 9 }}>
              <WidgetShell title="10. Приёмы по иммунопрофилактике — протоколы осмотра из МИС"
                stripe={D.violet} caveat={imm?.caveat} drill={() => openImmuno()}>
                {/* На пустом периоде пишем это прямо, а не молча пустой график:
                    caveat под заголовком есть всегда, и Empty его не дублирует. */}
                {(exp) => immLoading || !imm ? <Skel h={exp ? 320 : 210} />
                  : !imm.total.visits ? <NoData /> : (() => {
                  const pts = imm.points;
                  const gran = imm.granularity;
                  const places = bySort(imm.by_place, (r) => r.pct, !!worst.immuno);
                  const rows = exp ? places : places.slice(0, 8);
                  const option = {
                    backgroundColor: "transparent",
                    grid: { left: 52, right: 46, top: 26, bottom: gran === "day" ? 40 : 26 },
                    tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, ...TOOLTIP,
                      formatter: (ps: any) => {
                        const i = ps?.[0]?.dataIndex ?? 0;
                        const pt = pts[i];
                        if (!pt) return "";
                        const unknown = pt.visits - pt.known;
                        return `<b>${pt.label}</b><br/>Приёмов: ${fmt(pt.visits)}`
                          + (pt.known ? `<br/>С прививкой в тот же день: ${fmt(pt.with_vac)} (${fmtPct(pt.pct)})`
                                      + `<br/>Без прививки: ${fmt(pt.known - pt.with_vac)}` : "")
                          + (unknown ? `<br/><span style="color:${D.hint}">прививки за ${fmt(unknown)} приёмов ещё не выгружены</span>` : "")
                          + `<br/><span style="color:${D.hint}">документов: ${fmt(pt.docs)}</span>`;
                      } },
                    legend: { data: ["С прививкой", "Без прививки", "Прививки не выгружены",
                                     "Доля с прививкой"],
                      textStyle: { color: D.axis, fontSize: exp ? 13 : 10 },
                      top: 0, itemWidth: 10, itemHeight: 8 },
                    xAxis: { type: "category", data: pts.map((x) => x.label), ...AXIS_STYLE,
                      splitLine: { show: false },
                      axisLabel: { color: D.axis, fontSize: exp ? 12 : 9, interval: 0,
                        rotate: gran === "day" ? (exp ? 0 : 60) : 0, hideOverlap: true } },
                    yAxis: [
                      { type: "value", ...VALUE_AXIS },
                      // Вторая шкала — проценты, всегда 0–100: иначе линия доли
                      // прыгала бы по своему масштабу и читалась как рост приёмов.
                      { type: "value", min: 0, max: 100, position: "right",
                        ...AXIS_STYLE, splitLine: { show: false },
                        axisLabel: { color: D.yellow, fontSize: exp ? 12 : 9,
                          formatter: (v: number) => `${v}%` } },
                    ],
                    series: [
                      { name: "С прививкой", type: "bar", stack: "v",
                        itemStyle: { color: D.green },
                        data: pts.map((x) => (x.stale ? null : x.with_vac)) },
                      { name: "Без прививки", type: "bar", stack: "v",
                        itemStyle: { color: "#4a596e", borderRadius: [2, 2, 0, 0] },
                        data: pts.map((x) => (x.stale ? null : x.known - x.with_vac)) },
                      // Третий цвет — приёмы за дни, по которым прививок ещё нет
                      // в выгрузке. Красить их как «без прививки» было бы обманом.
                      { name: "Прививки не выгружены", type: "bar", stack: "v",
                        itemStyle: { color: "#2c3c52", borderRadius: [2, 2, 0, 0] },
                        data: pts.map((x) => (x.stale ? x.visits : x.visits - x.known)) },
                      { name: "Доля с прививкой", type: "line", yAxisIndex: 1, smooth: true,
                        symbol: "circle", symbolSize: exp ? 7 : 4,
                        lineStyle: { color: D.yellow, width: exp ? 2.4 : 1.6 },
                        itemStyle: { color: D.yellow },
                        data: pts.map((x) => x.pct) },
                    ],
                  };
                  return (
                    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "stretch" }}>
                      <div style={{ flex: "3 1 460px", minWidth: 0 }}>
                        <Chart option={option} height={exp ? "44vh" : 210}
                          onClick={(x: any) => {
                            const pt = pts[x?.dataIndex ?? -1];
                            if (pt) openImmuno({ date_from: pt.from, date_to: dayBefore(pt.to),
                                                 year: undefined, month: undefined });
                          }} />
                        <div style={{ borderTop: `0.5px solid ${W.divider}`, marginTop: 8, paddingTop: 7,
                          display: "flex", flexWrap: "wrap", gap: exp ? 18 : 10,
                          fontSize: exp ? 15 : 11.5 }}>
                          <span style={{ color: D.label }}>Приёмов:{" "}
                            <b style={{ color: SHELL.text }}>{fmt(imm.total.visits)}</b></span>
                          <span style={{ color: D.label }}>Людей:{" "}
                            <b style={{ color: SHELL.text }}>{fmt(imm.total.people)}</b></span>
                          <span style={{ color: D.label }}>Документов:{" "}
                            <b style={{ color: SHELL.text }}>{fmt(imm.total.docs)}</b></span>
                          <span style={{ color: D.label }}>С прививкой в тот же день:{" "}
                            <b style={{ color: D.greenText }}>{fmt(imm.total.with_vac)}</b>
                            {" · "}<b style={{ color: D.yellow }}>{fmtPct(imm.total.pct)}</b></span>
                          {imm.total.known !== imm.total.visits && (
                            <span style={{ color: D.hint }}>
                              доля — по {fmt(imm.total.known)} приёмам, где прививки уже выгружены
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ flex: "1 1 250px", minWidth: 0 }}>
                        <SortToggle worstFirst={!!worst.immuno} onToggle={() => flipKey("immuno")} />
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: exp ? 15 : 11 }}>
                          <thead>
                            <tr style={{ color: "#5a6d84", fontSize: exp ? 12 : 8.5, textAlign: "left" }}>
                              <td style={{ paddingBottom: 3 }}>Подразделение</td>
                              <td style={{ paddingBottom: 3, textAlign: "right" }}>Приёмов</td>
                              <td style={{ paddingBottom: 3, textAlign: "right" }}>С прививкой</td>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r) => (
                              <tr key={r.place} className="vac-row-drill"
                                title={`${r.place} — открыть приёмы этого подразделения`}
                                onClick={() => openImmuno({ place: r.place })}>
                                <td style={{ padding: "2px 0", maxWidth: exp ? 420 : 150, overflow: "hidden",
                                  textOverflow: "ellipsis", whiteSpace: "nowrap", color: D.label }}>{r.place}</td>
                                <td style={{ padding: "2px 0", textAlign: "right", color: SHELL.text }}>{fmt(r.visits)}</td>
                                <td style={{ padding: "2px 0", textAlign: "right", color: D.yellow }}
                                  title={r.known !== r.visits
                                    ? `доля по ${r.known} приёмам с выгруженными прививками` : undefined}>
                                  {fmtPct(r.pct)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {!exp && places.length > rows.length && (
                          <div style={{ color: D.hint, fontSize: 9.5, marginTop: 4 }}>
                            и ещё {places.length - rows.length} — разверните виджет
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </WidgetShell>
            </div>
          )}

        </div>{/* конец панели-«разворота» */}
      </div>
    </div>
  );
}

// ═══════════════════════ DRILL-ТАБЛИЦА (отдельное окно) ═══════════════════════
type CardRow = {
  iin: string; doc_id?: string | null;
  date: string | null; area: string | null; contingent: string | null;
  vac_type: string | null; preparation: string | null; status: string | null;
  age: number | null; sex: string | null; doctor: string | null;
};

const tdS: React.CSSProperties = { padding: "6px 10px", textAlign: "center", borderTop: `1px solid ${SHELL.border}`, whiteSpace: "nowrap" };

// ── Переход в МИС по строке таблицы ──
// Устроено как в Д-учёте. На 31.07.2026 МИС ручку для прививочных документов ещё не
// поднял: проверено вживую, docURLByUID и docVacURLByUID отдают 404 на прививочный UID
// (на документах АПП тот же docURLByUID работает — значит доступ и креды исправны).
// Переход предусмотрен заранее: когда МИС реализует, менять фронт не придётся.
async function openVacMisDoc(docId: string, misId: string): Promise<void> {
  if (!docId || !misId) return;
  try {
    const res = await fetch(
      `${apiBase()}/api/v1/vaccination/dashboard/mis-doc-url?docId=${encodeURIComponent(docId)}&mis=${encodeURIComponent(misId)}`,
      { headers: authHeaders(), credentials: "omit" },
    );
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      alert(d?.detail || `Не удалось открыть документ (HTTP ${res.status})`);
      return;
    }
    const data = await res.json();
    if (data?.url) window.open(data.url, "_blank", "noopener");
  } catch (e: any) {
    alert(`Не удалось открыть документ: ${e?.message || e}`);
  }
}

export function VaccinationCardsPage(): JSX.Element {
  const qs = new URLSearchParams(window.location.search);
  const [rows, setRows] = useState<CardRow[]>([]);
  const [total, setTotal] = useState(0);
  const [trunc, setTrunc] = useState(false);
  const [caveat, setCaveat] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<1 | -1>(1);

  // подпись среза собираем из тех же параметров, что пришли из виджета
  const bits: string[] = [];
  ["status", "age_group", "area", "contingent", "vac_type", "prep"].forEach((k) => {
    const v = qs.get(k); if (v) bits.push(v);
  });
  const title = "Прививки" + (bits.length ? " · " + bits.join(" · ") : "");

  useEffect(() => {
    const p: Record<string, any> = {};
    qs.forEach((v, k) => { p[k] = v; });
    api<{ rows: CardRow[]; total: number; truncated: boolean; caveat?: string }>("/api/v1/vaccination/dashboard/cards", p)
      .then((d) => { setRows(d.rows || []); setTotal(d.total || 0); setTrunc(!!d.truncated); setCaveat(d.caveat || null); })
      .catch((e) => setErr(String(e?.message || e))).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cols = ["№", "ИИН", "Дата", "Участок", "Контингент", "Вид", "Препарат", "Статус", "Возр.", "Пол", "Врач"];
  const sortKeys: (string | null)[] = [null, "iin", "date", "area", "contingent", "vac_type", "preparation", "status", "age", "sex", "doctor"];

  const toggleSort = (key: string | null) => {
    if (!key) return;
    if (sortBy === key) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortBy(key); setSortDir(1); }
  };
  const shownRows = useMemo(() => {
    if (!sortBy) return rows;
    const out = [...rows];
    out.sort((a: any, b: any) => {
      const x = a[sortBy], y = b[sortBy];
      const xe = x === null || x === undefined || x === "";
      const ye = y === null || y === undefined || y === "";
      if (xe && ye) return 0;
      if (xe) return 1;
      if (ye) return -1;
      if (typeof x === "number" && typeof y === "number") return (x - y) * sortDir;
      return String(x).localeCompare(String(y), "ru") * sortDir;
    });
    return out;
  }, [rows, sortBy, sortDir]);

  const ru = (d: string | null) => (d ? d.slice(0, 10).split("-").reverse().join(".") : "");
  const csv = () => {
    const esc = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [cols.map(esc).join(";")];
    shownRows.forEach((r, i) => lines.push([i + 1, r.iin, ru(r.date), r.area, r.contingent, r.vac_type,
      r.preparation, r.status, r.age, r.sex, r.doctor].map(esc).join(";")));
    const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "vaccination_cards.csv"; a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <div style={{ minHeight: "100vh", background: SHELL.page, padding: 14, color: SHELL.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <GlobalCss />
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4, flexWrap: "wrap" }}>
          <b style={{ fontSize: 17, color: "#fff" }}>{title}</b>
          <span style={{ color: SHELL.textMuted, fontSize: 13 }}>
            {loading ? "загрузка…" : `записей: ${fmt(total)}${trunc ? ` (показано ${fmt(rows.length)})` : ""}`}
          </span>
          <button className="vac-btn" onClick={csv} disabled={!rows.length}
            style={{ marginLeft: "auto", background: D.blue3, border: "none", fontWeight: 600 }}>
            Скачать CSV
          </button>
        </div>
        {caveat && <div style={{ color: D.hint, fontSize: 11.5, marginBottom: 10 }}>
          <i className="ti ti-info-circle" style={{ fontSize: 11, marginRight: 4 }} />{caveat}
        </div>}
        {err && <div style={{ color: D.redText, padding: 8 }}>{err}</div>}
        <div className="vac-scroll-thin" style={{ overflowX: "auto", border: `1px solid ${SHELL.border}`, borderRadius: 10 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, background: SHELL.card }}>
            <thead>
              <tr>{cols.map((cl, i) => {
                const key = sortKeys[i];
                const active = key !== null && sortBy === key;
                return (
                  <th key={i} onClick={() => toggleSort(key)} title={key ? "Сортировать по столбцу" : undefined}
                    style={{ background: "#0a3d66", padding: "8px 10px", position: "sticky", top: 0,
                      whiteSpace: "nowrap", textAlign: i <= 1 ? "left" : "center", color: "#fff",
                      cursor: key ? "pointer" : "default", userSelect: "none" }}>
                    {cl}{active ? (sortDir === 1 ? " ▲" : " ▼") : ""}
                  </th>
                );
              })}</tr>
            </thead>
            <tbody>
              {shownRows.map((r, i) => (
                <tr key={r.iin + String(i)} className={r.doc_id ? "vac-row-drill" : undefined}
                  title={r.doc_id ? "Открыть документ в МИС" : undefined}
                  onClick={r.doc_id ? () => openVacMisDoc(r.doc_id as string, qs.get("mis_id") || "") : undefined}
                  style={{ background: i % 2 ? "rgba(75,159,232,0.06)" : "transparent" }}>
                  <td style={tdS}>{i + 1}</td>
                  <td style={{ ...tdS, textAlign: "left" }}>{r.iin}</td>
                  <td style={tdS}>{ru(r.date)}</td>
                  <td style={tdS}>{r.area ?? ""}</td>
                  <td style={{ ...tdS, textAlign: "left", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }}
                    title={r.contingent ?? ""}>{r.contingent ?? ""}</td>
                  <td style={tdS}>{r.vac_type ?? ""}</td>
                  <td style={{ ...tdS, textAlign: "left", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }}
                    title={r.preparation ?? ""}>{r.preparation ?? ""}</td>
                  <td style={{ ...tdS, color: r.status === "Проведена" ? D.greenText : D.redText, fontWeight: 600 }}>{r.status ?? ""}</td>
                  <td style={tdS}>{r.age ?? ""}</td>
                  <td style={tdS}>{r.sex ?? ""}</td>
                  <td style={{ ...tdS, textAlign: "left" }}>{r.doctor ?? ""}</td>
                </tr>
              ))}
              {!loading && !rows.length && (
                <tr><td colSpan={cols.length} style={{ padding: 16, textAlign: "center", color: SHELL.textMuted }}>Нет данных</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── ПОФАМИЛЬНЫЙ СПИСОК ПРОСРОЧКИ ─────────────────────────────────────────────
// Отдельная страница, а не режим таблицы прививок. Причина не в удобстве:
// таблица прививок показывает записи о СДЕЛАННЫХ прививках, а здесь люди,
// у которых прививки НЕТ — записи о них не существует, и одним набором колонок
// эти две сущности не описать. Раньше плитка «просрочено за 7 дней» звала
// таблицу прививок без фильтров и открывала всё подряд со статусом «Проведена».
//
// Строка = человек и ОДНО просроченное требование: у одного человека их бывает
// несколько, и каждое закрывается отдельной прививкой.
type OverdueRow = {
  iin: string; birth: string | null; age: number | null; sex: string | null;
  area: string | null; row: string; group: string; due: string; days: number;
};

export function VaccinationOverduePage(): JSX.Element {
  const qs = new URLSearchParams(window.location.search);
  const [rows, setRows] = useState<OverdueRow[]>([]);
  const [total, setTotal] = useState(0);
  const [people, setPeople] = useState(0);
  const [trunc, setTrunc] = useState(false);
  const [caveat, setCaveat] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<1 | -1>(1);
  // Участок выбирается прямо здесь: со списком работают участковые, и гонять их
  // обратно на дашборд ради смены участка бессмысленно. Начальное значение —
  // из адреса (если пришли с уже выбранным участком).
  const [area, setArea] = useState<string>(qs.get("area") || "");
  const [areas, setAreas] = useState<string[]>([]);

  const bits: string[] = [];
  if (qs.get("days")) bits.push(`срок прошёл за ${qs.get("days")} дн.`);
  if (qs.get("kind") === "none") bits.push("не выполнено ни одно требование");
  if (qs.get("group")) bits.push(String(qs.get("group")));
  if (qs.get("row")) bits.push(String(qs.get("row")));
  if (area) bits.push(area);
  if (qs.get("plan_year")) bits.push(`план ${qs.get("plan_year")}`);
  const title = "Просроченные прививки" + (bits.length ? " · " + bits.join(" · ") : "");

  // Справочник участков — тот же, что и на дашборде (из текущего прикрепления).
  useEffect(() => {
    api<{ areas: string[] }>("/api/v1/vaccination/dashboard/bootstrap",
                             { mis_id: qs.get("mis_id") })
      .then((d) => setAreas(d.areas || [])).catch(() => setAreas([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoading(true); setErr("");
    const p: Record<string, any> = {};
    qs.forEach((v, k) => { p[k] = v; });
    // участок берём из состояния: он мог смениться уже здесь
    if (area) p.area = area; else delete p.area;
    api<{ rows: OverdueRow[]; total: number; people: number; truncated: boolean; caveat?: string }>(
      "/api/v1/vaccination/dashboard/overdue", p)
      .then((d) => {
        setRows(d.rows || []); setTotal(d.total || 0); setPeople(d.people || 0);
        setTrunc(!!d.truncated); setCaveat(d.caveat || null);
      })
      .catch((e) => setErr(String(e?.message || e))).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [area]);

  const cols = ["№", "ИИН", "Возр.", "Пол", "Участок", "Что просрочено", "Инфекция", "Срок", "Дней"];
  const sortKeys: (string | null)[] = [null, "iin", "age", "sex", "area", "row", "group", "due", "days"];
  const toggleSort = (key: string | null) => {
    if (!key) return;
    if (sortBy === key) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortBy(key); setSortDir(1); }
  };
  const shownRows = useMemo(() => {
    if (!sortBy) return rows;
    const out = [...rows];
    out.sort((a: any, b: any) => {
      const x = a[sortBy], y = b[sortBy];
      if (typeof x === "number" && typeof y === "number") return (x - y) * sortDir;
      return String(x ?? "").localeCompare(String(y ?? ""), "ru") * sortDir;
    });
    return out;
  }, [rows, sortBy, sortDir]);

  const ru = (d: string | null) => (d ? d.slice(0, 10).split("-").reverse().join(".") : "");
  const csv = () => {
    const esc = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [cols.map(esc).join(";")];
    shownRows.forEach((r, i) => lines.push([i + 1, r.iin, r.age, r.sex, r.area, r.row, r.group,
      ru(r.due), r.days].map(esc).join(";")));
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "vaccination_overdue.csv"; a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div style={{ minHeight: "100vh", background: SHELL.page, padding: 14, color: SHELL.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <GlobalCss />
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4, flexWrap: "wrap" }}>
          <b style={{ fontSize: 17, color: "#fff" }}>{title}</b>
          <span style={{ color: SHELL.textMuted, fontSize: 13 }}>
            {loading ? "загрузка…"
              : `требований: ${fmt(total)} у ${fmt(people)} человек${trunc ? ` (показано ${fmt(rows.length)})` : ""}`}
          </span>
          <select value={area} onChange={(e) => setArea(e.target.value)} disabled={loading}
            title="Участок прикрепления"
            style={{ height: 30, borderRadius: 8, border: `1px solid ${SHELL.border}`,
              background: "#083264", color: "#fff", padding: "0 8px", fontSize: 13, maxWidth: 260 }}>
            <option value="">Все участки</option>
            {areas.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <button className="vac-btn" onClick={csv} disabled={!rows.length}
            style={{ marginLeft: "auto", background: D.blue3, border: "none", fontWeight: 600 }}>
            Скачать CSV
          </button>
        </div>
        {caveat && <div style={{ color: D.hint, fontSize: 11.5, marginBottom: 10 }}>
          <i className="ti ti-info-circle" style={{ fontSize: 11, marginRight: 4 }} />{caveat}
        </div>}
        {err && <div style={{ color: D.redText, padding: 8 }}>{err}</div>}
        <div className="vac-scroll-thin" style={{ overflowX: "auto", border: `1px solid ${SHELL.border}`, borderRadius: 10 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, background: SHELL.card }}>
            <thead>
              <tr>{cols.map((cl, i) => {
                const key = sortKeys[i];
                const active = key !== null && sortBy === key;
                return (
                  <th key={i} onClick={() => toggleSort(key)} title={key ? "Сортировать по столбцу" : undefined}
                    style={{ background: "#0a3d66", padding: "8px 10px", position: "sticky", top: 0,
                      whiteSpace: "nowrap", textAlign: i <= 1 ? "left" : "center", color: "#fff",
                      cursor: key ? "pointer" : "default", userSelect: "none" }}>
                    {cl}{active ? (sortDir === 1 ? " ▲" : " ▼") : ""}
                  </th>
                );
              })}</tr>
            </thead>
            <tbody>
              {shownRows.map((r, i) => (
                <tr key={r.iin + r.row + String(i)}
                  style={{ background: i % 2 ? "rgba(75,159,232,0.06)" : "transparent" }}>
                  <td style={tdS}>{i + 1}</td>
                  <td style={{ ...tdS, textAlign: "left" }}>{r.iin}</td>
                  <td style={tdS}>{r.age ?? ""}</td>
                  <td style={tdS}>{r.sex ?? ""}</td>
                  <td style={tdS}>{r.area ?? ""}</td>
                  <td style={{ ...tdS, textAlign: "left", fontWeight: 600 }}>{r.row}</td>
                  <td style={{ ...tdS, textAlign: "left", maxWidth: 300, overflow: "hidden",
                    textOverflow: "ellipsis" }} title={r.group}>{r.group}</td>
                  <td style={tdS}>{ru(r.due)}</td>
                  {/* Чем дольше просрочено, тем краснее: свежие — жёлтые, старше года — красные */}
                  <td style={{ ...tdS, fontWeight: 600,
                    color: r.days >= 365 ? D.redText : r.days >= 90 ? D.yellow : D.hint }}>{r.days}</td>
                </tr>
              ))}
              {!loading && !rows.length && (
                <tr><td colSpan={cols.length} style={{ padding: 16, textAlign: "center", color: SHELL.textMuted }}>Нет данных</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── МЕДОТВОДЫ ────────────────────────────────────────────────────────────────
// Третий вид документа: не прививка и не просрочка. Ни одна таблица его не
// показывала, поэтому плитка «отказов» и виджет 6 никуда не вели — открывать
// было нечего.
type ExemptionRow = {
  iin: string; date: string | null; age: number | null; sex: string | null;
  area: string | null; exempt_from: string | null; vaccine_group: string | null;
  reason: string; kind: string; valid_until: string | null; issued_at: string | null;
};

export function VaccinationExemptionsPage(): JSX.Element {
  const qs = new URLSearchParams(window.location.search);
  const [rows, setRows] = useState<ExemptionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [trunc, setTrunc] = useState(false);
  const [caveat, setCaveat] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<1 | -1>(1);
  const [area, setArea] = useState<string>(qs.get("area") || "");
  const [areas, setAreas] = useState<string[]>([]);

  const bits: string[] = [];
  if (qs.get("reason")) bits.push(String(qs.get("reason")));
  if (qs.get("permanent") === "true") bits.push("постоянные");
  if (qs.get("active") === "true") bits.push("действующие");
  if (area) bits.push(area);
  const title = "Медотводы и отказы" + (bits.length ? " · " + bits.join(" · ") : "");

  useEffect(() => {
    api<{ areas: string[] }>("/api/v1/vaccination/dashboard/bootstrap",
                             { mis_id: qs.get("mis_id") })
      .then((d) => setAreas(d.areas || [])).catch(() => setAreas([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoading(true); setErr("");
    const p: Record<string, any> = {};
    qs.forEach((v, k) => { p[k] = v; });
    if (area) p.area = area; else delete p.area;
    api<{ rows: ExemptionRow[]; total: number; truncated: boolean; caveat?: string }>(
      "/api/v1/vaccination/dashboard/exemptions", p)
      .then((d) => { setRows(d.rows || []); setTotal(d.total || 0);
                     setTrunc(!!d.truncated); setCaveat(d.caveat || null); })
      .catch((e) => setErr(String(e?.message || e))).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [area]);

  const cols = ["№", "ИИН", "Дата", "Возр.", "Пол", "Участок", "Отвод от",
                "Вид", "Действует до", "Причина", "Место выдачи"];
  // номера колонок, значения которых прижаты влево
  const LEFT_COLS = new Set([1, 6, 9, 10]);
  const keys: (string | null)[] = [null, "iin", "date", "age", "sex", "area",
                                   "exempt_from", "kind", "valid_until", "reason", "issued_at"];
  const toggleSort = (k: string | null) => {
    if (!k) return;
    if (sortBy === k) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortBy(k); setSortDir(1); }
  };
  const shownRows = useMemo(() => {
    if (!sortBy) return rows;
    const out = [...rows];
    out.sort((a: any, b: any) => {
      const x = a[sortBy], y = b[sortBy];
      if (typeof x === "number" && typeof y === "number") return (x - y) * sortDir;
      return String(x ?? "").localeCompare(String(y ?? ""), "ru") * sortDir;
    });
    return out;
  }, [rows, sortBy, sortDir]);

  const ru = (d: string | null) => (d ? d.slice(0, 10).split("-").reverse().join(".") : "");
  const csv = () => {
    const esc = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [cols.map(esc).join(";")];
    shownRows.forEach((r, i) => lines.push([i + 1, r.iin, ru(r.date), r.age, r.sex, r.area,
      r.exempt_from, r.kind, ru(r.valid_until), r.reason, r.issued_at].map(esc).join(";")));
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "vaccination_exemptions.csv"; a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div style={{ minHeight: "100vh", background: SHELL.page, padding: 14, color: SHELL.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <GlobalCss />
      <div style={{ maxWidth: 1500, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4, flexWrap: "wrap" }}>
          <b style={{ fontSize: 17, color: "#fff" }}>{title}</b>
          <span style={{ color: SHELL.textMuted, fontSize: 13 }}>
            {loading ? "загрузка…" : `записей: ${fmt(total)}${trunc ? ` (показано ${fmt(rows.length)})` : ""}`}
          </span>
          <select value={area} onChange={(e) => setArea(e.target.value)} disabled={loading}
            title="Участок прикрепления"
            style={{ height: 30, borderRadius: 8, border: `1px solid ${SHELL.border}`,
              background: "#083264", color: "#fff", padding: "0 8px", fontSize: 13, maxWidth: 260 }}>
            <option value="">Все участки</option>
            {areas.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <button className="vac-btn" onClick={csv} disabled={!rows.length}
            style={{ marginLeft: "auto", background: D.blue3, border: "none", fontWeight: 600 }}>
            Скачать CSV
          </button>
        </div>
        {caveat && <div style={{ color: D.hint, fontSize: 11.5, marginBottom: 10 }}>
          <i className="ti ti-info-circle" style={{ fontSize: 11, marginRight: 4 }} />{caveat}
        </div>}
        {err && <div style={{ color: D.redText, padding: 8 }}>{err}</div>}
        <div className="vac-scroll-thin" style={{ overflowX: "auto", border: `1px solid ${SHELL.border}`, borderRadius: 10 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, background: SHELL.card }}>
            <thead>
              {/* Заголовок выравниваем ТАК ЖЕ, как значение под ним. Колонки без
                  ограничения ширины забирают весь остаток места: «Причина»
                  растягивалась на 824px, и центрированный заголовок уезжал
                  от своих значений почти на пол-экрана. */}
              <tr>{cols.map((cl, i) => (
                <th key={i} onClick={() => toggleSort(keys[i])}
                  style={{ background: "#0a3d66", padding: "8px 10px", position: "sticky", top: 0,
                    whiteSpace: "nowrap", color: "#fff",
                    textAlign: LEFT_COLS.has(i) ? "left" : "center",
                    cursor: keys[i] ? "pointer" : "default", userSelect: "none" }}>
                  {cl}{keys[i] && sortBy === keys[i] ? (sortDir === 1 ? " ▲" : " ▼") : ""}
                </th>
              ))}</tr>
            </thead>
            <tbody>
              {shownRows.map((r, i) => (
                <tr key={r.iin + String(i)} style={{ background: i % 2 ? "rgba(75,159,232,0.06)" : "transparent" }}>
                  <td style={tdS}>{i + 1}</td>
                  <td style={{ ...tdS, textAlign: "left" }}>{r.iin}</td>
                  <td style={tdS}>{ru(r.date)}</td>
                  <td style={tdS}>{r.age ?? ""}</td>
                  <td style={tdS}>{r.sex ?? ""}</td>
                  <td style={tdS}>{r.area ?? ""}</td>
                  <td style={{ ...tdS, textAlign: "left", maxWidth: 320, overflow: "hidden",
                    textOverflow: "ellipsis" }} title={r.exempt_from ?? ""}>{r.exempt_from ?? ""}</td>
                  {/* Постоянный отвод снимает требование совсем — выделяем цветом */}
                  <td style={{ ...tdS, fontWeight: 600,
                    color: r.kind === "постоянный" ? D.redText : D.yellow }}>{r.kind}</td>
                  <td style={tdS}>{r.kind === "постоянный" ? "—" : ru(r.valid_until)}</td>
                  <td style={{ ...tdS, textAlign: "left", maxWidth: 260, overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: r.reason === "Не указана" ? D.hint : undefined }}
                    title={r.reason}>{r.reason}</td>
                  <td style={{ ...tdS, textAlign: "left", maxWidth: 240, overflow: "hidden",
                    textOverflow: "ellipsis" }} title={r.issued_at ?? ""}>{r.issued_at ?? ""}</td>
                </tr>
              ))}
              {!loading && !rows.length && (
                <tr><td colSpan={cols.length} style={{ padding: 16, textAlign: "center", color: SHELL.textMuted }}>Нет данных</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── УЧАСТКИ ПО ОХВАТУ ────────────────────────────────────────────────────────
// Плитка «участков ниже среднего» показывает не людей и не документы, а УЧАСТКИ,
// поэтому вести её в таблицу прививок было бы неправдой. Здесь список самих
// участков, и данные берутся из уже посчитанного ответа виджетов — своей ручки
// не нужно, а ответ лежит в кэше и приходит мгновенно.
export function VaccinationAreasPage(): JSX.Element {
  const qs = new URLSearchParams(window.location.search);
  const belowOnly = qs.get("below") === "true";
  const [rows, setRows] = useState<FacilityRow[]>([]);
  const [district, setDistrict] = useState<number | null>(null);
  const [minDue, setMinDue] = useState<number>(50);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sortBy, setSortBy] = useState<string>("coverage");
  const [sortDir, setSortDir] = useState<1 | -1>(1);

  useEffect(() => {
    const p: Record<string, any> = {};
    qs.forEach((v, k) => { if (!["below"].includes(k)) p[k] = v; });
    api<Widgets>("/api/v1/vaccination/dashboard/widgets", p)
      .then((d) => {
        setRows(d.w3_map?.areas || []);
        setDistrict(d.w2_age_groups?.total_pct ?? null);
        setMinDue(d.w5_rating?.min_due ?? 50);
      })
      .catch((e) => setErr(String(e?.message || e))).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shown = useMemo(() => {
    // Тот же отбор, что и у плитки: участки со слишком малым знаменателем
    // исключены — на них охват скачет от 0 до 100 и ничего не значит.
    let out = rows.filter((r) => r.coverage !== null && (r.due ?? 0) >= minDue
                                 && r.name !== "Участок не указан");
    if (belowOnly && district !== null) out = out.filter((r) => (r.coverage ?? 0) < district);
    return [...out].sort((a: any, b: any) => {
      const x = a[sortBy], y = b[sortBy];
      if (typeof x === "number" && typeof y === "number") return (x - y) * sortDir;
      return String(x ?? "").localeCompare(String(y ?? ""), "ru") * sortDir;
    });
  }, [rows, belowOnly, district, minDue, sortBy, sortDir]);

  const title = (belowOnly ? "Участки с охватом ниже среднего по району" : "Участки по охвату")
    + (district !== null ? ` · средний ${fmtPct(district)}` : "");
  const cols = ["№", "Участок", "Население", "Срок наступил", "Привиты полностью",
                "Охват", "Выполнение плана", "Привито за период"];
  const keys = [null, "name", "population", "due", "covered", "coverage", "plan_pct", "vaccinated_pct"];
  const toggleSort = (k: string | null) => {
    if (!k) return;
    if (sortBy === k) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortBy(k); setSortDir(1); }
  };
  const csv = () => {
    const esc = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [cols.map(esc).join(";")];
    shown.forEach((r, i) => lines.push([i + 1, r.name, r.population, r.due, r.covered,
      r.coverage, r.plan_pct, r.vaccinated_pct].map(esc).join(";")));
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "vaccination_areas.csv"; a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div style={{ minHeight: "100vh", background: SHELL.page, padding: 14, color: SHELL.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <GlobalCss />
      <div style={{ maxWidth: 1300, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4, flexWrap: "wrap" }}>
          <b style={{ fontSize: 17, color: "#fff" }}>{title}</b>
          <span style={{ color: SHELL.textMuted, fontSize: 13 }}>
            {loading ? "загрузка…" : `участков: ${fmt(shown.length)}`}
          </span>
          <button className="vac-btn" onClick={csv} disabled={!shown.length}
            style={{ marginLeft: "auto", background: D.blue3, border: "none", fontWeight: 600 }}>
            Скачать CSV
          </button>
        </div>
        <div style={{ color: D.hint, fontSize: 11.5, marginBottom: 10 }}>
          <i className="ti ti-info-circle" style={{ fontSize: 11, marginRight: 4 }} />
          Охват = доля людей участка, у которых выполнены все наступившие требования.
          Участки, где срок наступил менее чем у {minDue} человек, исключены — на них охват даёт выбросы.
        </div>
        {err && <div style={{ color: D.redText, padding: 8 }}>{err}</div>}
        <div className="vac-scroll-thin" style={{ overflowX: "auto", border: `1px solid ${SHELL.border}`, borderRadius: 10 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, background: SHELL.card }}>
            <thead>
              <tr>{cols.map((cl, i) => (
                <th key={i} onClick={() => toggleSort(keys[i])}
                  style={{ background: "#0a3d66", padding: "8px 10px", position: "sticky", top: 0,
                    whiteSpace: "nowrap", textAlign: i <= 1 ? "left" : "center", color: "#fff",
                    cursor: keys[i] ? "pointer" : "default", userSelect: "none" }}>
                  {cl}{sortBy === keys[i] ? (sortDir === 1 ? " ▲" : " ▼") : ""}
                </th>
              ))}</tr>
            </thead>
            <tbody>
              {shown.map((r, i) => (
                <tr key={r.name} style={{ background: i % 2 ? "rgba(75,159,232,0.06)" : "transparent" }}>
                  <td style={tdS}>{i + 1}</td>
                  <td style={{ ...tdS, textAlign: "left" }}>{r.name}</td>
                  <td style={tdS}>{fmt(r.population)}</td>
                  <td style={tdS}>{fmt(r.due ?? null)}</td>
                  <td style={tdS}>{fmt(r.covered ?? null)}</td>
                  <td style={{ ...tdS, fontWeight: 600, color: covColor(r.coverage, district) }}>
                    {fmtPct(r.coverage)}
                  </td>
                  <td style={tdS}>{fmtPct(r.plan_pct ?? null)}</td>
                  <td style={tdS}>{fmtPct(r.vaccinated_pct ?? null)}</td>
                </tr>
              ))}
              {!loading && !shown.length && (
                <tr><td colSpan={cols.length} style={{ padding: 16, textAlign: "center", color: SHELL.textMuted }}>Нет данных</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── ТАБЛИЦА ПРИЁМОВ ПО ИММУНОПРОФИЛАКТИКЕ ────────────────────────────────────
// Отдельная страница, а не режим таблицы прививок: здесь строки — ПРОТОКОЛЫ
// ОСМОТРА из потока МИС (повод обращения «Иммунопрофилактика»), у них другой
// набор полей — услуга, МКБ, подразделение — и другой источник (база дефектолога).
// Прививка тут не сущность строки, а признак: была ли она у этого человека
// в этот же день.
type ImmunoRow = {
  date: string; iin: string; age: number | null; area: string | null;
  service_code: string | null; service_name: string | null;
  mkb: string | null; mkb_name: string | null;
  doctor: string | null; place: string | null; place_of_service: string | null;
  vac: boolean; vac_unknown?: boolean; vac_prep: string | null; vac_near: string | null;
  doc_id: string | null; marks: string[];
};

// Переход в МИС по протоколу осмотра. Ручка ДРУГАЯ, не та, что у прививочных
// документов: `docURLByUID` на документах АПП работает (проверено вживую
// 26.08.2026 — 5 из 5 отдали ссылку на «Документ.ПротоколОсмотра»), тогда как
// на прививочный UID МИС до сих пор отвечает 404.
async function openImmunoMisDoc(docId: string, misId: string, docDate?: string | null): Promise<void> {
  if (!docId || !misId) return;
  try {
    const q = new URLSearchParams({ docId, mis: misId });
    if (docDate) q.set("docDate", String(docDate).slice(0, 10));
    const res = await fetch(`${apiBase()}/api/v1/vaccination/dashboard/immuno-doc-url?${q.toString()}`,
      { headers: authHeaders(), credentials: "omit" });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      alert(d?.detail || `Не удалось открыть документ (HTTP ${res.status})`);
      return;
    }
    const data = await res.json();
    if (data?.url) window.open(data.url, "_blank", "noopener");
  } catch (e: any) {
    alert(`Не удалось открыть документ: ${e?.message || e}`);
  }
}

export function VaccinationImmunoPage(): JSX.Element {
  const qs = new URLSearchParams(window.location.search);
  const [rows, setRows] = useState<ImmunoRow[]>([]);
  const [total, setTotal] = useState(0);
  // Сколько документов в периоде всего — приходит, когда сработал предел выборки:
  // иначе в шапке стояло бы само значение предела, как будто столько и было.
  const [totalPeriod, setTotalPeriod] = useState<number | null>(null);
  const [trunc, setTrunc] = useState(false);
  const [caveat, setCaveat] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<1 | -1>(1);
  // «Все / только с прививкой / только без» — тот же срез, что линия на виджете
  const [withVac, setWithVac] = useState<string>(qs.get("with_vac") || "");

  const bits: string[] = [];
  ["area", "contingent", "place", "date_from", "date_to"].forEach((k) => {
    const v = qs.get(k); if (v) bits.push(v);
  });
  const title = "Приёмы по иммунопрофилактике" + (bits.length ? " · " + bits.join(" · ") : "");

  useEffect(() => {
    setLoading(true); setErr("");
    const p: Record<string, any> = {};
    qs.forEach((v, k) => { p[k] = v; });
    if (withVac) p.with_vac = withVac; else delete p.with_vac;
    api<{ rows: ImmunoRow[]; total: number; total_period?: number | null;
          truncated: boolean; caveat?: string }>(
      "/api/v1/vaccination/dashboard/immuno-cards", p)
      .then((d) => { setRows(d.rows || []); setTotal(d.total || 0);
        setTotalPeriod(d.total_period ?? null);
        setTrunc(!!d.truncated); setCaveat(d.caveat || null); })
      .catch((e) => setErr(String(e?.message || e))).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withVac]);

  const cols = ["№", "Дата", "ИИН", "Лет", "Участок", "Услуга", "Наименование услуги",
    "МКБ", "Диагноз", "Врач", "Подразделение", "Прививка в тот же день"];
  const sortKeys: (string | null)[] = [null, "date", "iin", "age", "area", "service_code",
    "service_name", "mkb", "mkb_name", "doctor", "place", "vac"];
  // Ширины колонок заданы явно и в процентах, а таблица — table-layout:fixed.
  // Иначе nowrap на длинных названиях услуг и диагнозов растягивал таблицу шире
  // экрана и появлялась горизонтальная прокрутка. Теперь длинные тексты
  // переносятся по словам, а «Лет» занимает ровно столько, сколько нужно
  // двум-трём цифрам (было около 60 px из-за подписи «Возр.»).
  const colW = ["2%", "5%", "8%", "1.6%", "7%", "6%", "14.9%", "4%", "19%", "11%", "9%", "12.5%"];
  // Ячейки этой таблицы переносят текст (общий tdS держит nowrap — с ним
  // при table-layout:fixed длинные диагнозы просто обрезались бы).
  const tdW: React.CSSProperties = { padding: "5px 6px", textAlign: "left",
    borderTop: `1px solid ${SHELL.border}`, whiteSpace: "normal",
    wordBreak: "break-word", lineHeight: 1.25, verticalAlign: "top" };
  const tdC: React.CSSProperties = { ...tdW, textAlign: "center" };
  const tdAge: React.CSSProperties = { ...tdC, padding: "5px 1px" };

  const toggleSort = (key: string | null) => {
    if (!key) return;
    if (sortBy === key) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortBy(key); setSortDir(1); }
  };
  const shownRows = useMemo(() => {
    if (!sortBy) return rows;
    const out = [...rows];
    out.sort((a: any, b: any) => {
      const x = a[sortBy], y = b[sortBy];
      const xe = x === null || x === undefined || x === "";
      const ye = y === null || y === undefined || y === "";
      if (xe && ye) return 0;
      if (xe) return 1;
      if (ye) return -1;
      if (typeof x === "number" && typeof y === "number") return (x - y) * sortDir;
      if (typeof x === "boolean" && typeof y === "boolean") return ((x ? 1 : 0) - (y ? 1 : 0)) * sortDir;
      return String(x).localeCompare(String(y), "ru") * sortDir;
    });
    return out;
  }, [rows, sortBy, sortDir]);

  const ru = (d: string | null) => (d ? d.slice(0, 10).split("-").reverse().join(".") : "");
  // За дни, по которые выгрузка прививок ещё не дошла, «нет» было бы неправдой.
  const vacText = (r: ImmunoRow) => r.vac_unknown
    ? "нет данных о прививках за этот день"
    : r.vac
    ? (r.vac_prep ? `да · ${r.vac_prep}` : "да")
    : (r.vac_near ? `нет · прививка ${ru(r.vac_near)}` : "нет");
  const csv = () => {
    const esc = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [cols.concat(["Метки"]).map(esc).join(";")];
    shownRows.forEach((r, i) => lines.push([i + 1, ru(r.date), r.iin, r.age, r.area, r.service_code,
      r.service_name, r.mkb, r.mkb_name, r.doctor, r.place, vacText(r),
      (r.marks || []).join(", ")].map(esc).join(";")));
    const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "immunoprofilaktika.csv"; a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <div style={{ minHeight: "100vh", background: SHELL.page, padding: 14, color: SHELL.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <GlobalCss />
      <div style={{ maxWidth: 1700, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4, flexWrap: "wrap" }}>
          <b style={{ fontSize: 17, color: "#fff" }}>{title}</b>
          <span style={{ color: SHELL.textMuted, fontSize: 13 }}>
            {loading ? "загрузка…"
              : `документов: ${fmt(totalPeriod ?? total)}${trunc ? ` (показано ${fmt(rows.length)})` : ""}`}
          </span>
          <select className="vac-select" title="Прививка в тот же день" value={withVac}
            onChange={(e) => setWithVac(e.target.value)} style={{ marginLeft: 8 }}>
            <option value="">Все приёмы</option>
            <option value="yes">Только с прививкой</option>
            <option value="no">Только без прививки</option>
          </select>
          <button className="vac-btn" onClick={csv} disabled={!rows.length}
            style={{ marginLeft: "auto", background: D.blue3, border: "none", fontWeight: 600 }}>
            Скачать CSV
          </button>
        </div>
        {caveat && <div style={{ color: D.hint, fontSize: 11.5, marginBottom: 10 }}>
          <i className="ti ti-info-circle" style={{ fontSize: 11, marginRight: 4 }} />{caveat}
        </div>}
        {err && <div style={{ color: D.redText, padding: 8 }}>{err}</div>}
        {/* Прокрутка — только внутри этого блока, страница по горизонтали не едет.
            minWidth оставлен, чтобы на узком экране колонки не сплющивались
            до нечитаемого, а появлялась прокрутка здесь же. */}
        <div className="vac-scroll-thin" style={{ overflowX: "auto", border: `1px solid ${SHELL.border}`, borderRadius: 10 }}>
          <table style={{ width: "100%", minWidth: 1150, tableLayout: "fixed",
            borderCollapse: "collapse", fontSize: 13, background: SHELL.card }}>
            <colgroup>{colW.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
            <thead>
              <tr>{cols.map((cl, i) => {
                const key = sortKeys[i];
                const active = key !== null && sortBy === key;
                return (
                  <th key={i} onClick={() => toggleSort(key)}
                    title={key ? (i === 3 ? "Возраст на дату приёма · сортировать" : "Сортировать по столбцу") : undefined}
                    style={{ background: "#0a3d66", padding: i === 3 ? "8px 1px" : "8px 6px",
                      position: "sticky", top: 0, lineHeight: 1.2,
                      fontSize: i === 3 ? 11 : undefined,
                      whiteSpace: "normal", textAlign: i <= 2 ? "left" : "center", color: "#fff",
                      cursor: key ? "pointer" : "default", userSelect: "none" }}>
                    {cl}{active ? (sortDir === 1 ? " ▲" : " ▼") : ""}
                  </th>
                );
              })}</tr>
            </thead>
            <tbody>
              {shownRows.map((r, i) => (
                <tr key={(r.doc_id || r.iin) + String(i)} className={r.doc_id ? "vac-row-drill" : undefined}
                  title={r.doc_id ? "Открыть протокол осмотра в МИС" : undefined}
                  onClick={r.doc_id ? () => openImmunoMisDoc(r.doc_id as string, qs.get("mis_id") || "", r.date) : undefined}
                  style={{ background: i % 2 ? "rgba(75,159,232,0.06)" : "transparent" }}>
                  <td style={tdC}>{i + 1}</td>
                  <td style={{ ...tdW, whiteSpace: "nowrap" }}>{ru(r.date)}</td>
                  <td style={tdW}>
                    {r.iin}
                    {(r.marks || []).length > 0 && (
                      <span style={{ color: D.hint, fontSize: 10.5 }}> · {(r.marks || []).join(", ")}</span>
                    )}
                  </td>
                  <td style={tdAge}>{r.age ?? ""}</td>
                  <td style={tdW} title={r.area ?? ""}>{r.area ?? ""}</td>
                  <td style={{ ...tdC, whiteSpace: "nowrap" }}>{r.service_code ?? ""}</td>
                  <td style={tdW} title={r.service_name ?? ""}>{r.service_name ?? ""}</td>
                  <td style={{ ...tdC, whiteSpace: "nowrap" }}>{r.mkb ?? ""}</td>
                  <td style={tdW} title={r.mkb_name ?? ""}>{r.mkb_name ?? ""}</td>
                  <td style={tdW} title={r.doctor ?? ""}>{r.doctor ?? ""}</td>
                  <td style={tdW} title={r.place ?? ""}>{r.place ?? ""}</td>
                  <td style={{ ...tdW,
                    color: r.vac_unknown ? D.axis : r.vac ? D.greenText : D.hint,
                    fontWeight: r.vac ? 600 : 400 }}
                    title={vacText(r)}>{vacText(r)}</td>
                </tr>
              ))}
              {!loading && !rows.length && (
                <tr><td colSpan={cols.length} style={{ padding: 16, textAlign: "center", color: SHELL.textMuted }}>Нет данных</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
